/**
 * RFQ Builder / item triage — prototype `vRFQ()`, contract §5–6, infra C.3.
 *
 * The purchaser's working screen: confirm what the AI matched, decide per line whether it
 * is priced from a contract or sent to suppliers, fix quantities and UOMs, assign
 * suppliers (one item → many suppliers), then generate one RFQ per supplier.
 *
 * FE/BE split as specified:
 *   FE  — UOM conversion display, the qty × factor > on-hand warning, confidence bars,
 *         and the RFQ-split preview (all pure functions of data already on screen).
 *   BE  — extraction + confidence, UOM master validity (422), RFQ generation and sending.
 */

import { useSyncItemsFromErp } from '../../lib/api/erpSync'
import { useCompanyBlanketOrders } from '../../lib/api/useCompanyBlanketOrders'
import { useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Badge } from '../../components/Badge'
import { ReadOnlyGate } from '../../components/ReadOnlyGate'
import { lockReason } from '../../lib/readOnly'
import { ContextBar } from '../../components/ContextBar'
import { Button, Card, PageHead, Placeholder } from '../../components/ui'
import { useToast } from '../../components/Toast'
import { useEnquiry, useRfqs } from '../../lib/api/useEnquiries'
import {
  fetchItemByCode,
  fetchSuppliersForItem,
  useBlanketOrderItems,
  useItemPrices,
  useItemPriceRows,
  useStockByCode,
  useItemsByCode,
  useMatchItems,
  useSearchAvailable,
  useSupplierEmails,
  type ItemMatch,
  fetchBlanketOrders,
} from '../../lib/api/useItems'

/** Stable identity so `matches.get(...)` does not hand the rows a new Map every render. */
const EMPTY_MATCHES: ReadonlyMap<string, ItemMatch> = new Map()
import { useSuppliers } from '../../lib/api/useSuppliers'
import { describeTagged, tagWritesForCorrection, useUpdateItemTags } from '../../lib/api/itemTags'
import { useEmailTemplate } from '../../lib/api/useSettings'
import { renderEmailTemplate } from '../../lib/emailTemplate'
import { useSession } from '../../components/SessionProvider'
import { isLiveFeature, NOT_AVAILABLE_YET } from '../../lib/api/liveAvailability'
import {
  useAddLine,
  useAddLines,
  useAssignSuppliers,
  useDeleteLine,
  useDeleteLines,
  fetchSentRfqIds,
  useGenerateRfqs,
  useMatchLines,
  useSendRfq,
  useUpdateLine,
  useUpdateLines,
} from '../../lib/api/useEnquiryMutations'
import {
  buildRfqDrafts,
  defaultRfqNote,
  groupBySupplier,
  isSelectable,
  mergeItemEnrichment,
  sendRfqDrafts,
  summarizeRfqGeneration,
  triageCounts,
  linesWithoutQty,
  unassignedRfqLines,
  uomOptions,
  type RfqDraft,
  sentRfqByRow,
  existingLineForItem,
  pricingAfterItemChange,
  withoutGreetings,
} from '../../lib/rfq'
import { uomAfterItemChange } from '../../lib/packSize'
import { withoutInternalAnnotations } from '../../lib/internalNotes'
import { IN_HOUSE_SUPPLIER, planStockPricing, priceFromStock } from '../../lib/directPricing'
import { inHousePriceFor, type InHousePrice } from '../../lib/inHousePrice'
import { useItemScope } from '../../lib/api/itemScope'
import { rematchPlan } from '../../lib/rematch'
import { rematchText } from '../../lib/api/useItems'
import { useSyncApprovedUoms } from '../../lib/api/uomSync'
import { isUomConfirmed, uomConfirmationNote, uomNeedsChoice, type UomChoice } from '../../lib/uomChoice'
import { describeSelectedLines } from '../../lib/supplierAssignment'
import { describeRowLinkError } from '../../lib/uom'
import { describeMandatory, enquiryHeaderGaps } from '../../lib/quotationMandatory'
import { fetchUomMaster } from '../../lib/api/useUoms'
import { useQueryClient } from '@tanstack/react-query'
import { EnquiryHeaderCard } from './EnquiryHeaderCard'
import { SourcePanel } from './SourcePanel'
import { enquiryFilesKey, uploadEnquiryFile, useEnquiryFiles } from '../../lib/api/enquiryFiles'
import { LineRow } from './LineRow'
import { useLineSuggestions } from './useLineSuggestions'
import { mergeMatchIntoSuggestion } from './suggestions'
import { AutoMatcher } from './AutoMatcher'
import { codesMissingFromMaster } from './autoMatch'
import { buildContractQuotationLines, contractLinesTotal, everyEnquiryLine } from '../../lib/contractQuotation'
import { matchesLineSearch } from '../../lib/lineSearch'
import { countUndecided, filterForTriage, type TriageFilter } from '../../lib/lineTriageSort'
import { readDocument } from '../../lib/readDocument'
import { dropUnknownCodes } from '../../lib/itemCodes'
import { uploadLine } from '../../lib/uploadLine'
import { knownItemCodes } from '../../lib/api/useMailExtraction'
import { describeRead } from '../../lib/readMailItems'
import { SearchBox } from '../../components/SearchBox'
import { usePricingRules } from '../../lib/api/useSettings'
import { useCustomerCurrency, useDeliveryLocationPriceLists, useExchangeRates } from '../../lib/api/useMasters'
import { useCreateQuotation, useExistingQuotation } from '../../lib/api/useQuotation'
import { pickRate } from '../../lib/exchangeRates'
import { baseCurrencyFor } from '../../lib/api/baseCurrency'
import { BlanketOrderBar } from './BlanketOrderBar'
import { ContractApplier } from './ContractApplier'
import { buildRateIndex, contractsForItem, planContractLines, ratesForLine, revertContractLines, routeChangePatch, selectedOrdersForLines, needsContractLookup } from '../../lib/blanketOrders'
import { CatalogPickerModal } from './CatalogPickerModal'
import { RequestItemLink } from './RequestItemLink'
import { ItemHistoryModal } from './ItemHistoryModal'
import { SupplierPickerModal } from './SupplierPickerModal'
import { AddItemModal } from './AddItemModal'
import { SendRfqModal } from './SendRfqModal'
import type { Currency, EnquiryItem, EnquiryItemPatch, Item, NewEnquiryItem, UomConversion } from '../../lib/types'

type Dialog =
  | { kind: 'match'; lineId: string }
  | { kind: 'bulk-match' }
  | { kind: 'line-suppliers'; lineId: string }
  | { kind: 'bulk-suppliers' }
  | { kind: 'item-history'; lineId: string }
  | { kind: 'add-item' }
  | null

export function RfqPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [selected, setSelected] = useState<string[]>([])
  /** View-only filter over the line table — see the search bar's own note below. */
  const [lineSearch, setLineSearch] = useState('')
  /*
    Which column the table is narrowed to. One at a time, because two filters at once is a state
    nobody can read off a table — and the two overlap heavily anyway.
  */
  const [triage, setTriage] = useState<TriageFilter | null>(null)
  const [dialog, setDialog] = useState<Dialog>(null)
  /** Disables "Accept all recommendations" for the duration of its own sequential writes. */
  const [applyingAll, setApplyingAll] = useState(false)
  /**
   * The RFQ mails being reviewed before sending; `null` while the dialog is closed. Held here
   * rather than in the dialog so an edited note survives a re-render of the table behind it.
   */
  const [drafts, setDrafts] = useState<RfqDraft[] | null>(null)
  /** Per-line 422 from the server's UOM check, cleared on the next successful edit. */
  const [uomErrors, setUomErrors] = useState<Record<string, { message: string; available: UomConversion[] }>>({})

  const enquiryQuery = useEnquiry(id)
  const rfqsQuery = useRfqs(id)
  // Lines a supplier is already pricing — their delete is disabled. See `sentRfqByRow`.
  const sentRows = useMemo(() => sentRfqByRow(rfqsQuery.data), [rfqsQuery.data])
  const enquiry = enquiryQuery.data
  const lines = useMemo(() => enquiry?.items ?? [], [enquiry])
  /*
    "Sync from ERP" (2026-10-26): the item, its prices and its stock, pulled now rather than
    whenever the connector next runs. One line or every matched line; the items, prices and stock
    are refetched afterwards (`erpSync.ts`).
  */
  const [syncingCodes, setSyncingCodes] = useState<ReadonlySet<string>>(new Set())
  const syncFromErp = useSyncItemsFromErp({
    onDone: (result) => {
      setSyncingCodes(new Set())
      toast(
        result.failed.length === 0
          ? `Synced ${result.codes.length} item${result.codes.length === 1 ? '' : 's'} from the ERP — prices and stock are current.`
          : `Synced from the ERP with ${result.failed.length} record(s) refused: ${result.failed.slice(0, 2).join('; ')}`,
      )
    },
    onError: (error) => {
      setSyncingCodes(new Set())
      toast(`Sync from the ERP failed — ${error.message}`)
    },
  })
  const startErpSync = (codes: string[]) => {
    const unique = [...new Set(codes.filter(Boolean))]
    if (unique.length === 0 || syncFromErp.isPending) return
    setSyncingCodes(new Set(unique))
    syncFromErp.mutate(unique)
  }
  /*
    What the TABLE shows. Everything else on this screen — the triage counts, the RFQ split, the
    contract totals, generation — keeps reading `lines`, because a filter is a way of looking at
    the enquiry, not a way of changing what is in it.
  */
  const shownLines = useMemo(
    // Both narrow, and they compose: search by text, then keep only the undecided of those.
    () => filterForTriage(lines.filter((l) => matchesLineSearch(l, lineSearch)), triage),
    [lines, lineSearch, triage],
  )

  /*
    Catalogue matches for the lines that have no item yet — fired automatically on arrival.

    ---- The key is built from EVERY line, not just the unmatched ones ----

    It used to key on the unmatched lines' texts, which looked right and was wrong: applying an
    item to one line makes it matched, so the key changed, React Query saw a different query and
    returned `undefined` — every OTHER line's candidates disappeared and a fresh ~20s call started
    for the smaller set. Matching one line wiped the answers for all the rest.

    Every line's text is stable while the enquiry's lines are, so the key holds still through a
    whole triage session: one fetch, results kept. Only the lines still lacking an item are
    actually SENT (read through a ref at fetch time, so it is the state at the moment of the call,
    not whatever it was when this render ran) — matching an already-matched line would be ~16s
    each for an answer nobody needs.
  */
  const unmatchedLines = lines.filter((l) => !l.item_code && l.raw_text.trim() !== '')
  // The customer's own words, without ANY note of ours. The unit annotation was measured to change
  // the top result from wrong to right; the "(code 61 26 11 not in catalogue)" note was still going
  // to the matcher and the catalogue search until 2026-10-16 — a number we could not resolve,
  // offered as part of the thing to find. `withoutInternalAnnotations` strips both.
  const matchText = (line: EnquiryItem) => withoutInternalAnnotations(line.raw_text)
  const unmatchedRef = useRef<string[]>([])
  /*
    Which enquiry's table has finished settling — the one-way latch behind the "preparing" panel.

    MUST be declared here, above the loading/error early-returns, like every other hook. It was
    declared beside the `ready` calculation instead, which sits after them: the loading render ran
    one hook fewer than the loaded one and React threw "Rendered more hooks than during the
    previous render". Its assignment stays with that calculation, since an assignment is not a
    hook.
  */
  const settledOnce = useRef<string | null>(null)
  unmatchedRef.current = unmatchedLines.map(matchText)

  // Availability is a property of the DEPLOYMENT (is a `/search` proxy configured?), not of the
  // build — see `useSearchAvailable`.
  const searchAvailable = useSearchAvailable()
  // Every item search from this page carries the enquiry's company and type (2026-09-21).
  useItemScope({ company: enquiry?.header.company ?? '', enquiryType: enquiry?.header.enquiry_type ?? '' })
  const matchQuery = useMatchItems(
    lines.map(matchText),
    searchAvailable.data === true,
    () => unmatchedRef.current,
  )
  const matches = matchQuery.data ?? EMPTY_MATCHES


  /*
    Rates for the DEFAULT match on each line, across the whole table, in one request.

    `useItemPrices` is a single `Item Price` query with an `in` filter, so batching here rather
    than in `LineRow` turns forty rows into one call. Only the top candidate per line is included:
    that is the only one a row renders now that the shortlist lives in `CatalogPickerModal`, and
    the picker asks for its own (one request, its own visible set) when it opens.
  */
  const candidateCodes = useMemo(
    () =>
      [...new Set([...matches.values()].map((m) => m.candidates[0]?.item_code).filter((c): c is string => !!c))],
    [matches],
  )
  const candidatePrices = useItemPrices(candidateCodes)

  /*
    Stock for every matched line, loaded WITH the items rather than per row on demand.

    `items.get_stock` still takes one code at a time (asked for as `D17`), so this is N requests —
    but they are fired together, they are cached per code, and they land inside the "preparing"
    panel instead of trickling in after the purchaser clicks a row. The old per-row latch meant
    the stock column read "—" until each row was engaged, which looked like missing data rather
    than unasked data.
  */
  const matchedCodes = useMemo(
    () => [...new Set(lines.map((l) => l.item_code).filter((c): c is string => !!c))],
    [lines],
  )
  const stockByCode = useStockByCode(matchedCodes, true)

  /*
    ---- Our own price, per LINE and per UNIT (2026-09-14) ----

    Every `Item Price` row for the matched items, resolved per line through `inHousePriceFor`:
    the delivery location's list first (the enquiry's `price_list`, else what the location
    implies), the line's own unit where a row exists for it, else derived through the master.
    This replaces `Item.base_price`, which `get_items_by_codes` sends with no unit — measured
    returning the CARTON price beside `stock_uom: PKT`, which is what made "40.00/PKT" appear
    and stay put when the line was switched to cartons.
  */
  const priceRows = useItemPriceRows(matchedCodes)
  const priceLists = useDeliveryLocationPriceLists()
  const preferredPriceList =
    enquiryQuery.data?.header.price_list ??
    (enquiryQuery.data?.header.delivery_location ? (priceLists.data?.get(enquiryQuery.data.header.delivery_location) ?? null) : null)
  const ourPriceFor = (line: EnquiryItem, uom: string = line.uom): InHousePrice | null => {
    const item = line.item_code ? itemsByCode.get(line.item_code) : undefined
    if (!item || !line.item_code) return null
    return inHousePriceFor(priceRows.data?.get(line.item_code), item, uom, preferredPriceList)
  }

  // One batch request for every matched item on the table, rather than one per row.
  // `preferredPriceList` travels on the request too (Akhil, 2026-09-15) — see `useItemsByCode`.
  const { byCode: itemsByCode, priceSource, resolvedCodes, isResolved: itemsResolved } = useItemsByCode(
    lines.map((l) => l.item_code).filter((c): c is string => !!c),
    preferredPriceList,
  )
  // A unit requested from a line and since APPROVED is pulled onto its item here (2026-09-22,
  // `uomSync.ts`); the unit chips then offer it on the next render.
  useSyncApprovedUoms(id, itemsByCode, (units) =>
    toast(`Unit${units.length === 1 ? '' : 's'} now available: ${units.map((u) => `${u.uom} on ${u.item}`).join(', ')}`),
  )

  /*
    ---- Units still to decide (Akhil, 2026-09-15) ----

    `uomNeedsChoice` reads the persisted signal (`qty_as_per_stock_uom`); `decidedUoms` is the
    session's memory of decisions taken while `B11` keeps every enquiry write failing, so the gate
    below can be passed at all. Placed AFTER `useItemsByCode` on purpose: `undecidedLines` runs
    during render and reads `itemsByCode`, and a `const` is not usable above its own declaration.
  */
  const [decidedUoms, setDecidedUoms] = useState<Set<string>>(() => new Set())
  /*
    Lines whose item the PURCHASER swapped for a different one this session (Akhil, 2026-09-17:
    "if we select a different item and that item's uom is not matching with the customer uom,
    let the user select the required uom"). On these the unit question is strict — PCS is not
    Nos — where the original match still takes the 2026-09-16 equivalence. Session memory, like
    `decidedUoms`: the swap is the moment to ask, and nothing on the row records it.
  */
  const [itemSwappedLines, setItemSwappedLines] = useState<Set<string>>(() => new Set())
  const noteItemSwap = (line: EnquiryItem, itemCode: string) => {
    if (line.item_code && line.item_code !== itemCode) setItemSwappedLines((current) => new Set(current).add(line.line_id))
  }
  const [flashLineId, setFlashLineId] = useState<string | null>(null)
  // Keyed on line AND item: changing the item reopens the question, as it should.
  const decisionKey = (line: EnquiryItem) => `${line.line_id}\u0000${line.item_code ?? ''}`
  const uomChoiceFor = (line: EnquiryItem): UomChoice | null => {
    if (decidedUoms.has(decisionKey(line))) return null
    const item = line.item_code ? itemsByCode.get(line.item_code) : undefined
    return uomNeedsChoice(line, item, { exact: itemSwappedLines.has(line.line_id) })
  }
  const undecidedLines = lines.filter((line) => uomChoiceFor(line) !== null)
  const decideUom = (line: EnquiryItem, uom: string) => {
    setDecidedUoms((current) => new Set(current).add(decisionKey(line)))
    const repriced = line.suppliers.includes(IN_HOUSE_SUPPLIER) ? ourPriceFor(line, uom) : null
    // `customer_uom` is NOT touched — it is the customer's record, not ours. The server derives
    // the stock quantity itself on save. The DECISION is recorded on the line's note (Akhil,
    // 2026-09-18: it used to be asked again after a visit to the quotation page) — see
    // `uomConfirmationNote` — so "keep" is a write too.
    updateLine.mutate({
      lineId: line.line_id,
      patch: {
        ...(uom !== line.uom ? { uom } : {}),
        ...(repriced !== null ? { selected_rate: repriced.amount } : {}),
        note: uomConfirmationNote(uom, line.item_code ?? '', uomChoiceFor(line)?.customerUnit ?? ''),
      },
    })
  }
  /**
   * Refuses to send or quote while a unit is undecided — and takes the purchaser to it. The first
   * such line is scrolled into view and outlined; the toast says how many remain.
   */
  const refuseUntilUnitsDecided = (action: string): boolean => {
    if (undecidedLines.length === 0) return false
    const first = undecidedLines[0]
    setFlashLineId(first.line_id)
    document.getElementById(`line-${first.line_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    window.setTimeout(() => setFlashLineId((current) => (current === first.line_id ? null : current)), 4000)
    toast(
      `Cannot ${action} yet — ${undecidedLines.length} line${undecidedLines.length === 1 ? '' : 's'} still need${
        undecidedLines.length === 1 ? 's' : ''
      } a unit decided. Taken you to line ${first.cust_sno || first.line_id}.`,
    )
    return true
  }


  /*
    ---- Standing contracts (blanket orders) ----

    `null` means "not yet decided in this session", which is what triggers recovery from the
    enquiry itself: the selection cannot be persisted (an unknown key on `update_enquiry` is
    silently dropped — measured), but each contract line's SUPPLIER can be, and is. So on first
    load the ticked contracts are rebuilt from the suppliers already priced on the enquiry. Once
    the purchaser touches the bar, their choice governs and recovery never runs again.
  */
  /** Set when the purchaser asks to see the table before it has settled — see the panel below. */
  const [revealEarly, setRevealEarly] = useState(false)
  const [blanketChoice, setBlanketChoice] = useState<string[] | null>(null)
  // This enquiry's company's contracts only (2026-10-26).
  const blanketOrders = useCompanyBlanketOrders(enquiry?.header.company)
  const recoveredOrders = useMemo(
    () => selectedOrdersForLines(blanketOrders.data ?? [], lines),
    [blanketOrders.data, lines],
  )
  const selectedOrders = blanketChoice ?? recoveredOrders
  const blanketItems = useBlanketOrderItems(selectedOrders)
  const rateIndex = useMemo(() => buildRateIndex(blanketItems.data), [blanketItems.data])

  /*
    EVERY active contract, indexed — not only the ones ticked.

    `rateIndex` above is built from the SELECTED orders and drives pricing; this one exists purely
    to answer "is there a better deal on a contract I have not selected?". Without it a purchaser
    had to tick contracts one at a time to find out, which is the opposite of the comparison they
    are trying to make.

    It costs NOTHING extra: `items.get_active_blanket_orders` returns all five active orders with
    their 420 rates INLINE and seeds them into the very cache `useBlanketOrderItems` reads
    (`useActiveBlanketOrders`' own note), so this resolves from memory rather than the wire.
  */
  const allOrderNames = useMemo(() => (blanketOrders.data ?? []).map((o) => o.name), [blanketOrders.data])
  const allBlanketItems = useBlanketOrderItems(allOrderNames)
  const allRateIndex = useMemo(() => buildRateIndex(allBlanketItems.data), [allBlanketItems.data])
  /*
    ---- Quoting the contract lines directly ----

    A blanket order fixes the rate, so a contract line has nothing to compete: no RFQ, no supplier
    quotation, no comparison. Most provision enquiries are ENTIRELY contract-priced, which makes
    this the ordinary route to a customer quotation rather than a shortcut around one.

    The same inputs the comparison screen prices with — the server's own margin, the customer's
    currency and the FX snapshot — so one quotation cannot be priced two different ways depending
    on which screen produced it.
  */
  const pricingRules = usePricingRules()
  const exchangeRates = useExchangeRates()
  const customerCurrency = useCustomerCurrency(enquiryQuery.data?.header.customer)
  const existingQuotation = useExistingQuotation(id)
  const createQuotation = useCreateQuotation({
    onSuccess: (name) => {
      toast(`Quotation ${name} created`)
      navigate(`/quote/${id}`)
    },
    onError: (error) => toast(error.message),
  })

  /*
    Point 9 — "apply blanket order for all item or selected item".

    Contracts used to price every line they matched, with no way to hold one back. That is right
    for a provision enquiry where the whole list is contracted, and wrong the moment a purchaser
    wants two of forty lines on contract and the rest out to RFQ: the only recovery was to let it
    price everything and then re-route the rest by hand, line by line.

    `'all'` stays the default because it is the common case and the previous behaviour.
  */
  const [blanketScope, setBlanketScope] = useState<'all' | 'selected'>('all')
  const contractScopeLines = useMemo(
    () => (blanketScope === 'selected' ? lines.filter((l) => selected.includes(l.line_id)) : lines),
    [blanketScope, lines, selected],
  )

  /*
    Codes the item master was asked for and does NOT have — the AI extraction sometimes writes a
    DESCRIPTION into `item_code` ("General Purpose Glue, 50ml"), and a line carrying one is
    undecided, not decided. Exactly the fact `LineRow` renders as "not in catalogue".

    Gated on `itemsResolved`: while the lookup is in flight every code looks missing, and handing
    that to `AutoMatcher` would have it overwrite good codes before their masters arrived.
  */
  const unresolvableCodes = useMemo(
    // Only codes THIS answer was asked for — a freshly picked one is not missing, merely unread.
    () => (itemsResolved ? codesMissingFromMaster(lines, itemsByCode, resolvedCodes) : new Set<string>()),
    [itemsResolved, lines, itemsByCode, resolvedCodes],
  )

  /** Still to be written. Shrinks to nothing once `ContractApplier` has committed them. */
  const contractPlan = useMemo(() => planContractLines(contractScopeLines, rateIndex), [contractScopeLines, rateIndex])
  /** Lines the selection prices AT ALL — written or not. This is what the bar reports, so the
   *  count does not collapse to zero the moment the write lands. Scoped, so the badge answers
   *  "what will this do" rather than "what could it have done". */
  const contractPricedCount = useMemo(
    // In the line’s unit only, as `planContractLines` applies them (point 2).
    () => contractScopeLines.filter((l) => ratesForLine(rateIndex, l).length > 0).length,
    [contractScopeLines, rateIndex],
  )

  /*
    ---- The picker's ranked matches, fetched on demand ----

    `matchQuery` above sends only the lines that still lack an item, which is right for the table
    but leaves the CHANGE-ITEM dialog empty in the most common case there is: every line has been
    auto-matched, so nothing was sent, so the dialog that exists to offer alternatives has none to
    offer. Reloading the page has the same effect — the lines come back matched.

    So the dialog asks for its own line. One text is ~0.8s (measured: the service costs ~0.32s per
    item plus overhead) on a deliberate click, cached per text, and skipped entirely whenever the
    table's own results already cover the line.
  */
  const dialogLineId = dialog && 'lineId' in dialog ? dialog.lineId : null
  const dialogLine = dialogLineId ? lines.find((l) => l.line_id === dialogLineId) : undefined
  const dialogMatchText = dialog?.kind === 'match' && dialogLine ? matchText(dialogLine) : ''
  const cachedDialogMatch = dialogMatchText ? matches.get(dialogMatchText) : undefined
  const needsDialogMatch = dialogMatchText !== '' && (cachedDialogMatch?.candidates.length ?? 0) === 0
  const dialogMatch = useMatchItems(
    needsDialogMatch ? [dialogMatchText] : [],
    needsDialogMatch && searchAvailable.data === true,
  )

  // The AI's outstanding per-field suggestions, keyed by line. Empty when there's no extraction
  // in this tab's session store (fresh tab, reload, colleague's session) — every row then renders
  // exactly as it did before suggestions existed.
  const suggestions = useLineSuggestions(enquiry?.header.mail_id ?? null, lines)

  // `line.suppliers` (and everything derived from it below) is keyed by `Supplier.code` — the
  // Link identity RFQ generation and `items.get_supplier_emails` need. This is the ONE place
  // that resolves a code to a display name for the whole page, so the table, the split preview
  // and the send dialog can never show one supplier under two different names.
  const suppliersQuery = useSuppliers(undefined, enquiryQuery.data?.header.company)
  const supplierNameByCode = useMemo(
    () => new Map((suppliersQuery.data?.data ?? []).map((s) => [s.code, s.name])),
    [suppliersQuery.data],
  )
  const supplierName = (code: string) => supplierNameByCode.get(code) ?? code

  const clearUomError = (lineId: string) =>
    setUomErrors((current) => {
      if (!current[lineId]) return current
      const next = { ...current }
      delete next[lineId]
      return next
    })

  /*
    The BULK line write, used only by `AutoMatcher`. No per-line `onSuccess` handling: a batch
    reports once, through `AutoMatcher`'s own summary toast, rather than firing one message per
    line. Errors surface through the mutation's own rejection at the call site.
  */
  const updateLines = useUpdateLines(id)

  const updateLine = useUpdateLine(id, {
    onSuccess: (line) => {
      clearUomError(line.line_id)
      // The server echoes the qty-vs-stock warning; surface it without blocking.
      line.warnings?.forEach((w) => toast(`⚠ ${w}`))
    },
    onError: (error, vars) => {
      if (error.status === 422 && error.code === 'UOM_NOT_IN_MASTER') {
        setUomErrors((current) => ({
          ...current,
          [vars.lineId]: { message: error.message, available: error.body?.available ?? [] },
        }))
        return
      }
      // `UOM_NOT_IN_MASTER` above is a MOCK-only synthesized code (`enquiry-writes.ts`'s `fail()`)
      // — a real backend rejects an unknown Link with a raw `LinkValidationError` message instead
      // (`Could not find Row #1: UOM: NO`), which names a row index this screen has no direct use
      // for. `describeRowLinkError` (`lib/uom.ts`) maps that back to the line the purchaser
      // actually sees, best-effort: `lines` is this screen's own read of `enquiry.items`, in the
      // same order the wholesale write was built from (`updateEnquiryItems`, `useEnquiryMutations.ts`).
      toast(describeRowLinkError(error.message, lines.map((l) => ({ description: withoutInternalAnnotations(l.raw_text) }))))
    },
  })

  // Every mutation reports its own failure — a silent write is worse than a wrong one.
  const deleteLine = useDeleteLine(id, {
    onSuccess: () => toast('Line removed'),
    onError: (error) => toast(error.message),
  })
  // Several ticked lines in one write (Akhil, 2026-09-21).
  const deleteLines = useDeleteLines(id, {
    onSuccess: (_, ids) => {
      setSelected([])
      toast(`${ids.length} line(s) removed`)
    },
    onError: (error) => toast(error.message),
  })
  const deleteSelected = () => {
    if (selected.length === 0) return
    if (!window.confirm(`Remove the ${selected.length} selected line(s) from this enquiry? This cannot be undone.`)) return
    deleteLines.mutate(selected)
  }

  /*
    "Use our price" for every line, or the ticked ones (Akhil, 2026-11-03: apply the base /
    in-stock price to all the items in the enquiry with one click). The row badge's write, in one
    batch — see `planStockPricing`. Each line is priced in ITS OWN unit (`ourPriceFor`); a line
    the catalogue has no price for is left as it is and counted.
  */
  const stockPricingPlan = useMemo(
    () =>
      planStockPricing(
        selected.length > 0 ? lines.filter((l) => selected.includes(l.line_id)) : lines,
        (line) => ourPriceFor(line)?.amount ?? null,
      ),
    // `ourPriceFor` reads these three.
    [lines, selected, itemsByCode, priceRows.data, preferredPriceList],
  )
  const applyOurPriceToLines = () => {
    const { actions, noPrice, replacing } = stockPricingPlan
    if (actions.length === 0) return
    const scope = selected.length > 0 ? 'selected line' : 'line'
    const question = [
      `Price ${actions.length} ${scope}${actions.length === 1 ? '' : 's'} from our own stock, at our catalogue price? They will not go out to RFQ.`,
      replacing > 0 ? `${replacing} of them already have a contract or supplier price, which will be replaced.` : '',
      noPrice > 0 ? `${noPrice} line${noPrice === 1 ? ' has' : 's have'} no price of ours and will stay as they are.` : '',
    ]
      .filter(Boolean)
      .join('\n\n')
    if (!window.confirm(question)) return
    updateLines.mutate(actions, {
      onSuccess: () =>
        toast(
          `${actions.length} line${actions.length === 1 ? '' : 's'} priced from our own stock — no RFQ for ${actions.length === 1 ? 'it' : 'them'}` +
            (noPrice > 0 ? ` · ${noPrice} without a price of ours left as they were` : ''),
        ),
      onError: (error) => toast(`Could not price the lines from our stock: ${error.message}`),
    })
  }

  /*
    Ask the matcher AGAIN — every line, or the ticked ones — under the enquiry's current company
    and type (Akhil, 2026-09-21: "if the enquiry type or company changes"). Fresh answers, then
    each line whose top hit changed takes it; nothing is blanked. See `lib/rematch.ts`.
  */
  const [rematching, setRematching] = useState(false)
  const rematch = async (only?: ReadonlySet<string>) => {
    const targets = lines.filter((l) => (!only || only.has(l.line_id)) && matchText(l).trim() !== '')
    if (targets.length === 0) return
    setRematching(true)
    try {
      const fresh = new Map<string, ItemMatch | null>()
      await Promise.all(targets.map(async (l) => fresh.set(l.line_id, await rematchText(matchText(l)).catch(() => null))))
      const plan = rematchPlan(targets, (l) => fresh.get(l.line_id) ?? null)
      if (plan.length === 0) {
        toast(`Matched ${targets.length} line(s) again under ${enquiry?.header.company || 'the company'} / ${enquiry?.header.enquiry_type} — nothing changed.`)
        return
      }
      const n = await applyItemsToLines(plan.map(({ lineId, itemCode, score }) => ({ lineId, itemCode, score })))
      toast(`Matched ${targets.length} line(s) again — ${n} changed item.`)
    } catch (error) {
      toast(`Could not re-match: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setRematching(false)
    }
  }
  const addLine = useAddLine(id, {
    onSuccess: (line) => {
      setDialog(null)
      toast(`Added ${line.item_code}`)
    },
    onError: (error) => toast(error.message),
  })
  const assignSuppliers = useAssignSuppliers(id, {
    onSuccess: (data, vars) => {
      // A remove or clear keeps the dialog open — the strip updates in place and the purchaser
      // may be taking several off. An add closes it, as it always did — and UNTICKS the lines,
      // as a bulk match does. It did not (Akhil, 2026-09-16): the ticks outlived the assign, so
      // the next one — a new line, ticked on its own — quietly went to every line ticked before,
      // and a supplier chosen for one item was mailed the whole enquiry.
      if ((vars.mode ?? 'add') === 'add') {
        setDialog(null)
        setSelected([])
      }
      toast(
        vars.mode === 'clear'
          ? `Suppliers cleared on ${data.items.length} line(s)`
          : vars.mode === 'remove'
            ? `${vars.suppliers[0]} removed from ${data.items.length} line(s)`
            : `Suppliers set on ${data.items.length} line(s)`,
      )
    },
    onError: (error) => toast(error.message),
  })
  /*
    Teaching the catalogue the customer's own wording for an item a human just corrected.

    ANNOUNCED, both ways (Akhil, 2026-09-11). The tag changes nothing visible on this screen — it
    pays off months later, as a better match — so left silent the correction looks like it went
    nowhere, and nobody would know the feature exists. The toast follows the match's own, and says
    what the tag buys rather than that a call succeeded.

    Still not chained to the write: the match is what the purchaser asked for, the tag is a hint
    for next month's matcher, and a tag that fails to save must never undo or block one that
    worked. A failure is reported as exactly that — a lost hint, not a lost correction.
  */
  const tagItems = useUpdateItemTags({
    onSuccess: (rows) => {
      const message = describeTagged(rows)
      if (message) toast(message)
    },
    onError: (error) => toast(`⚠ Item matched, but its tag was not saved: ${error.message}`),
  })

  const matchLines = useMatchLines(id, {
    onSuccess: (data) => {
      setDialog(null)
      setSelected([])
      toast(`${data.items.length} line(s) matched`)
    },
    onError: (error) => toast(error.message),
  })
  /*
    Reading an uploaded list — LOCALLY, with the same parsers the mail path uses.

    This used to POST the file to a mock-only `/api/v1/enquiries/:id/extract` and the button was
    disabled behind `enquiryExtractUpload` (`A3`), waiting on a server method. It never needed
    one: `readUploadedFile` opens xlsx/xlsm, legacy .xls, .doc and PDF in the browser, exactly as
    an emailed attachment is opened. The server-side alternative is dead anyway (`B1`).

    The lines are then added through the ordinary line-add path, so they arrive on the enquiry the
    same way a hand-added item does — one write, one invalidation, one set of guards.

    An IMAGE or a scanned PDF is the one thing those parsers cannot see; `readDocument` sends
    just those to Azure Document Intelligence and hands the OCR's tables to the same readers
    (2026-09-17). The toast says when a figure was read off a photo.
  */
  const addLinesForUpload = useAddLines(id)
  const queryClient = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const enquiryFiles = useEnquiryFiles(id)
  const extractUpload = async (file: File) => {
    setUploading(true)
    /*
      The file itself is kept on the enquiry (2026-11-03), so it can be opened again from "Matched
      from". Saved alongside the read, not after it: a list nothing could be read from is exactly
      the one worth keeping. A failed save is its own red toast and costs no lines.
    */
    const saving = uploadEnquiryFile(id, file).then(
      () => {
        void queryClient.invalidateQueries({ queryKey: enquiryFilesKey(id) })
        return true
      },
      (error: unknown) => {
        toast(`Could not keep ${file.name} on the enquiry: ${error instanceof Error ? error.message : 'unknown error'}. The lines are not affected.`)
        return false
      },
    )
    try {
      const read = await readDocument(file.name, new Uint8Array(await file.arrayBuffer()))
      // A line with no readable quantity comes through at 0 (Akhil, 2026-09-22: no dropping) —
      // the row asks for the figure; only a row with no wording at all is nothing.
      const found = read.items.filter((item) => item.description.trim() !== '')
      /*
        A code column is the CUSTOMER's code until the Item master says otherwise (2026-09-17:
        "Could not find Row #1: Item Code: 312381 …" on an uploaded screenshot). Unknown codes
        stay on the wording for the matcher; a failed lookup keeps the codes and lets the server
        have the final say, as the mail path does.
      */
      let usable = found
      let unknownCodes: string[] = []
      const codes = found.map((i) => i.item_code).filter((c): c is string => !!c)
      if (codes.length > 0) {
        try {
          const checked = dropUnknownCodes(found, await knownItemCodes(codes, queryClient))
          usable = checked.items
          unknownCodes = checked.rejected
        } catch {
          // Cannot prove any code bad — leave them.
        }
      }
      if (usable.length === 0) {
        // The same note the mail path shows, so an unreadable upload explains itself the same way.
        toast(describeRead(read))
        return
      }
      /*
        ONE write, every field (Akhil, 2026-09-17: "not able to extract all the items, most of the
        time a 500"). The parser was reading the sheets whole — 70 of 70 on a real requisition —
        and this handler then lost them: one wholesale write PER LINE, without the customer's
        wording, with the sheet's unit sent raw into a Link field. The first "KGS" the UOM master
        did not spell that way failed the write, and the lines that did land had nothing to match.

        Units are resolved against the real master (case-insensitively — `KG`, `Kg`, `KG.` are one
        unit) and travel as BOTH `uom` and `customer_uom`; a unit the master does not know goes as
        `null` and is named in the toast, for the purchaser to pick on the row.
      */
      const master = await fetchUomMaster(queryClient)
      const duplicates: string[] = []
      const unknownUnits = new Set<string>()
      const adds: NewEnquiryItem[] = []
      for (const item of usable) {
        const already = item.item_code ? existingLineForItem(lines, item.item_code) : null
        if (already) {
          duplicates.push(`${item.item_code} (line #${already.cust_sno})`)
          continue
        }
        // Never a null unit — see `uploadLine` for the placeholder rule the mail path shares.
        const { add, unknownUnit } = uploadLine(item, master)
        if (unknownUnit) unknownUnits.add(unknownUnit)
        adds.push(add)
      }
      const added = await addLinesForUpload.mutateAsync(adds)
      const kept = await saving
      toast(
        `Added ${added.length} line(s) from ${file.name}.${kept ? ' File kept under "Matched from".' : ''} ${
          read.vision ? `Read by OCR${read.vision === 'text' ? ' and the AI reader' : ''} — check quantities and units against the file. ` : describeRead(read)
        }${
          unknownUnits.size > 0 ? ` ${unknownUnits.size} unit(s) not in the UOM master — choose them on the rows: ${[...unknownUnits].join(', ')}.` : ''
        }${duplicates.length > 0 ? ` Skipped ${duplicates.length} already on the enquiry: ${duplicates.join(', ')}.` : ''}${
          unknownCodes.length > 0 ? ` ${unknownCodes.length} code(s) in the list are not ours (${unknownCodes.slice(0, 3).join(', ')}${unknownCodes.length > 3 ? '…' : ''}) — kept on the wording, match those lines.` : ''
        }`,
      )
    } catch (error) {
      toast(`Could not read ${file.name}: ${error instanceof Error ? error.message : 'unknown error'}`)
    } finally {
      setUploading(false)
    }
  }
  // Takes `lines` now: `rfq.generate_rfqs` is dead, so the per-supplier grouping it used to do
  // server-side happens client-side from exactly the lines this screen is showing. See
  // `useEnquiryMutations.ts`'s CLIENT-SIDE RFQ GENERATION section.
  const generateRfqs = useGenerateRfqs(id, lines)
  const sendRfq = useSendRfq(id)

  // Supplier emails for the To/Cc fields, resolved via `items.get_supplier_emails` — the only
  // source that actually works (`Supplier.primary_email` is always `''` live, defect B3; see
  // `useItems.ts`'s `useSupplierEmails`). Scoped to suppliers actually assigned to an RFQ line,
  // loaded with the screen so opening the send dialog is instant.
  const supplierCodes = useMemo(
    () => [...new Set(lines.filter((l) => l.route === 'rfq').flatMap((l) => l.suppliers))],
    [lines],
  )
  const supplierEmailsQuery = useSupplierEmails(supplierCodes)
  const emailBySupplier = useMemo(
    () => new Map(Object.entries(supplierEmailsQuery.data ?? {})),
    [supplierEmailsQuery.data],
  )

  // RFQ ids already generated THIS review session, by supplier — the point of caching this
  // rather than re-deriving it is `sendRfqDrafts`' whole reason for existing (review round 1,
  // IMPORTANT): a retry after a partial send failure must reuse the ids it already has, never
  // blindly re-run `generate_rfqs`, and must never re-send a supplier who already went out.
  // Cleared whenever the dialog is opened fresh, so a stale id from an earlier review never
  // leaks into a new one.
  const rfqIdsRef = useRef<Map<string, string>>(new Map())

  const headerGaps = enquiryHeaderGaps(enquiryQuery.data?.header ?? {})

  const openSendDialog = () => {
    if (refuseUntilUnitsDecided('send the RFQ')) return
    /*
      The enquiry header must already hold everything the QUOTATION will need (Akhil,
      2026-09-17): the doctype requires vessel, port, delivery location, sales person, purchaser
      and customer reference on every quotation, and the comparison builds the quotation from
      this enquiry — a gap found there is a round trip back here. So it is refused here, with the
      header card opened on the gaps.
    */
    const gaps = headerGaps
    if (gaps.length > 0) {
      toast(`Before sending an RFQ, complete the enquiry header — the quotation will need ${describeMandatory(gaps)}. Fill ${gaps.length === 1 ? 'it' : 'them'} in the Enquiry details card above.`)
      document.getElementById('enquiry-header-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    rfqIdsRef.current = new Map()
    /*
      The drafts show what `buildRfqPlan` will actually send: a supplier sent to before gets only
      the lines added since (Akhil, 2026-09-15), with a note of what they already have. Built from
      the same RFQ list the generator reads, so the dialog and the send cannot disagree.
    */
    const existing = (rfqsQuery.data ?? []).map((r) => ({
      rfq: r.id,
      supplier: r.supplier,
      sent_at: r.sent_at ?? null,
      rows: (r.items ?? []).map((i) => i.enquiry_row_no).filter((n) => n !== ''),
    }))
    const drafts = buildRfqDrafts(lines, id, enquiryQuery.data!.header, emailBySupplier, supplierName, templateNote, existing)
    if (drafts.length === 0) {
      toast('Every line is already with its supplier on a sent RFQ — nothing new to send.')
      return
    }
    setDrafts(drafts)
  }

  /**
   * The covering note, rendered from the server's own `Rfq` Email Template.
   *
   * Preferred over `defaultRfqNote` because the template states the business's rules — the
   * workbook must come back unmodified, any other format is rejected — which our note never did.
   * `send_rfq_email_with_excel` does not render the template itself (measured 2026-08-07: sending
   * with no `message` produced its own hardcoded default), so it reaches a supplier only if we
   * fill it in here.
   *
   * Returns `null` when the bench has no template, and `buildRfqDrafts` falls back — a send must
   * never depend on a template being present.
   */
  const session = useSession()
  const rfqTemplate = useEmailTemplate('Rfq')
  const templateNote = (_code: string, displayName: string): string | null => {
    const response = rfqTemplate.data?.response
    if (!response) return null
    const rendered = renderEmailTemplate(response, {
      supplier_name: displayName,
      // Our real covering note — vessel, port, "return the sheet completed". Without it the
      // template falls through to its OWN default, "Please find below the link to submit your
      // quotation", which is sent alongside an Excel attachment and NO link. The template
      // supplies the policy; this supplies the substance.
      message_for_supplier: defaultRfqNote(enquiryQuery.data!.header),
      user_fullname: session.user,
    })
    // `send_rfq` greets the supplier itself; the template's "<name>," and our "Dear Sirs," go.
    return rendered ? withoutGreetings(rendered, displayName) || null : null
  }

  const patchDraft = (supplier: string, patch: Partial<RfqDraft>) =>
    setDrafts((current) => current?.map((d) => (d.supplier === supplier ? { ...d, ...patch } : d)) ?? null)

  /**
   * Generate then send. `SendRfqModal` already disables its Send button while any draft has no
   * resolved recipient (`draftsWithoutRecipient`); `useSendRfq`'s own guard (see
   * `useEnquiryMutations.ts`) refuses an empty `to` a second time as a backstop.
   *
   * A partial failure PRUNES the succeeded drafts from `drafts` (not just this closure's local
   * variable — the state itself), so the button's retry click — `sendAllDrafts` again — only
   * ever sees what is still outstanding. Combined with `rfqIdsRef` above, a retry neither
   * re-generates nor re-mails a supplier who already has their RFQ.
   */
  const [sendingAll, setSendingAll] = useState(false)
  const sendAllDrafts = async () => {
    if (!drafts || drafts.length === 0) return
    setSendingAll(true)
    try {
      const result = await sendRfqDrafts(
        drafts,
        rfqIdsRef.current,
        async () => {
          const generated = await generateRfqs.mutateAsync()
          // All three outcomes, in one line, named: raised / already-sent / failed. A skip is
          // information — the run correctly refused to rewrite an RFQ a supplier already has —
          // but it means those lines did NOT go out, and the purchaser has to know that.
          const summary = summarizeRfqGeneration(
            { raised: generated.rfqs.length, skipped: generated.skipped, failed: generated.failed },
            supplierName,
          )
          // Nothing was raised — every supplier was already sent to, or every write failed. Stop
          // here rather than walk into the send with an empty id map, which would report the same
          // suppliers again as "no RFQ was generated for it" and bury the real reason.
          if (generated.rfqs.length === 0) throw new Error(summary)
          toast(summary)
          // The RFQs exist but the enquiry stayed where it was — a fact about this enquiry, not
          // about any one supplier, so it gets its own line.
          if (generated.status_error) toast(generated.status_error)
          // Skipped suppliers are deliberately NOT in the id map: they already have a sent RFQ,
          // and handing their id to the sender is how one gets mailed the same RFQ twice. They ARE
          // named separately, so `sendRfqDrafts` can prune their drafts instead of treating an
          // absent id as a failed write — the skip is already reported, accurately, by `summary`
          // above, and a second toast saying "no RFQ was generated for it" would be false.
          return {
            rfqIdBySupplier: new Map(generated.rfqs.map((r) => [r.supplier, r.id])),
            skipped: generated.skipped.map((s) => s.supplier),
          }
        },
        (payload) => sendRfq.mutateAsync(payload),
        // Re-read at the moment of a retry: the server may have mailed a supplier on the previous
        // attempt while we recorded a failure (timeout, 502, a body returned after the mail went).
        // Without this the retry mails them a second time — see `sendRfqDrafts`.
        () => fetchSentRfqIds(id),
      )
      rfqIdsRef.current = result.rfqIdBySupplier

      if (result.sentSuppliers.length > 0) {
        toast(`${result.sentSuppliers.length} RFQ${result.sentSuppliers.length === 1 ? '' : 's'} sent`)
      }
      result.failures.forEach((f) => toast(f.message))

      if (result.failures.length === 0) {
        rfqIdsRef.current = new Map()
        setDrafts(null)
        navigate(`/suppliers/${id}`)
      } else {
        // Skipped suppliers go too, not just sent ones: their RFQ is already out, there is nothing
        // left for a retry to do for them, and keeping them would re-run generate on every retry
        // just to skip them again.
        const done = new Set([...result.sentSuppliers, ...result.skippedSuppliers])
        setDrafts((current) => current?.filter((d) => !done.has(d.supplier)) ?? null)
      }
    } catch (error) {
      // `generate` itself failing (not a per-supplier send) — nothing was sent this round.
      toast(error instanceof Error ? error.message : 'Could not generate the RFQs')
    } finally {
      setSendingAll(false)
    }
  }

  /* ---------- states ---------- */

  if (enquiryQuery.isPending) return <Placeholder>Loading requirement…</Placeholder>

  if (enquiryQuery.error || !enquiry) {
    const error = enquiryQuery.error as Error | null
    return (
      <Placeholder>
        <b>Could not load {id}.</b> Check the mock backend is running (<b>npm run dev:all</b>).
        {error ? <div style={{ marginTop: 6 }}>{error.message}</div> : null}
      </Placeholder>
    )
  }

  const counts = triageCounts(lines)
  // Lines the reader brought through with no quantity (2026-09-22) — the send waits for them.
  const noQtyLines = linesWithoutQty(lines)
  const groups = groupBySupplier(lines)
  const unassigned = unassignedRfqLines(lines)

  /*
    Applying a matched item to a line — the ONE path every entry point goes through.

    Sets three things in a single write: the item, the unit, and the item's suppliers.

    ---- Why suppliers come along automatically ----

    A sixty-line enquiry is normal, and the old flow was: click the line, open the supplier
    dialog, tick the suppliers, save — per line. The list being ticked is the item's own supplier
    list, which the master already knows. So it is filled in, and the purchaser removes the odd
    one with the × on the chip. Add-them-all becomes remove-a-few.

    Nothing is sent to anyone by this: RFQs go out only from "Generate RFQs & send", and the
    chips are on the row to be seen and edited before that.

    ---- Why it costs no extra round trip ----

    The item master and the supplier list are two GETs run in PARALLEL, then ONE write carrying
    all three fields. The alternative — writing the code, then the unit, then the suppliers — is
    three sequential writes at ~1.7s each on this bench.
  */
  const buildItemPatch = async (line: EnquiryItem, itemCode: string, known?: Item): Promise<EnquiryItemPatch> => {
    /*
      ---- Route, on a Provision enquiry ----

      A `Provision` enquiry is mostly contract business, so a line whose item HAS an active
      blanket order defaults to `contract` rather than being sent to suppliers. That is the
      enquiry type saying what kind of work this is, and it saves setting the same route on
      line after line.

      Deliberately narrow, because contract-routing a line means it skips the RFQ entirely:

        - only on `Provision` — a Technical enquiry keeps RFQ even where a contract exists, since
          a technical buy is normally competed even when a standing rate is on file;
        - only when the item actually has an ACTIVE order (`toItemContracts` drops expired and
          cancelled ones), so the line always has a real price and a real supplier behind it;
        - never silently — the ROUTE dropdown sits on the row showing "Provision · contract", one
          click from "Send RFQ".

      ---- Suppliers ----

      A contract line needs none: the blanket order names its own supplier, which is what the row
      shows ("fixed by contract"), what RFQ generation honours (`isRfqLine` excludes it), and what
      the bulk actions skip. So the lookup is skipped for a line that is contract-routed already
      or is about to be.
    */
    const provision = enquiry.header.enquiry_type === 'Provision'
    /*
      Contracts come from the SELECTED blanket orders, not a per-item request.

      This used to call `items.get_blanket_orders(itemCode)` for every line being matched — one
      round trip per line, on the slowest step of the page, for an answer that is empty unless the
      item happens to be contracted. The purchaser now names the contracts once
      (`BlanketOrderBar`) and this is a Map lookup. An enquiry with no contracts selected simply
      finds nothing, which is exactly what the request used to return for most items anyway.
    */
    let contracts = provision ? contractsForItem(rateIndex, itemCode) : []
    /*
      The index is EMPTY for the first seconds after the page loads — `get_active_blanket_orders`
      and the selection recovered from the lines both have to land. An item changed in that
      window used to be priced as if it had no contract at all (route `rfq`, rate 0, no supplier)
      while the row's own offer list showed the contract it had just discarded (2026-09-23).
      Only an answer from LOADED data may be trusted; before that the item's contracts are read
      from the server. Never behind a purchaser's own selection — see `needsContractLookup`.
    */
    if (needsContractLookup({ provision, found: contracts.length, touched: blanketChoice !== null, ready: !blanketOrders.isLoading && !blanketItems.isPending })) {
      contracts = await fetchBlanketOrders(itemCode).catch(() => [])
    }
    const item = known ?? (await fetchItemByCode(itemCode))
    const changed = line.item_code !== itemCode
    /*
      A CHANGED item takes its own pricing (Akhil, 2026-09-16): its contract on a Provision
      enquiry, else RFQ at no price — the old item's contract rate, stock price, past-price
      supplier and route must not survive onto it. `pricingAfterItemChange` decides; suppliers
      are fetched for the RFQ case only. A re-confirmed item keeps everything it had, and its
      suppliers are only ever ADDED (an empty answer must not clear a purchaser's choice).
    */
    const willBeContract = provision && contracts.length > 0
    const wantsSuppliers = changed ? !willBeContract : line.route !== 'contract' && !willBeContract
    const suppliers = wantsSuppliers ? await fetchSuppliersForItem(itemCode) : []
    const pricing = pricingAfterItemChange({ changed, provision, contracts, suppliers })
    const uom = item
      ? uomAfterItemChange(line.uom, matchText(line), uomOptions(item).map((c) => c.uom), item.base_uom, line.customer_uom)
      : null
    return {
      item_code: itemCode,
      ...pricing,
      ...(!changed && willBeContract && line.route !== 'contract' ? { route: 'contract' as const } : {}),
      ...(uom ? { uom } : {}),
      // A unit confirmed for the OLD item is not a confirmation for this one — see `isUomConfirmed`.
      ...(changed && isUomConfirmed(line) ? { note: null } : {}),
      // Only when the master actually knows some — an empty array would CLEAR suppliers a
      // purchaser had already chosen, since `suppliers` replaces wholesale.
      ...(!changed && suppliers.length > 0 ? { suppliers } : {}),
    }
  }

  /**
   * The matcher's score for a code, if it offered that code for this line.
   *
   * Needed because the score is PERSISTED with the match (see `AutoMatchAction.score`), which
   * makes a stale one actively wrong: pick a different item by hand and the stored score would go
   * on describing the item it replaced — a 3% bar under a deliberate human choice, or worse, a
   * 92% bar under something the matcher never proposed.
   *
   * `1` when the code was not among the candidates: a purchaser choosing an item from the
   * catalogue IS the confident case, and it is the same value the mail reader stamps.
   */
  const scoreForCode = (line: EnquiryItem, itemCode: string): number => {
    // BOTH lists the matcher answered for this line's text — the table's and the dialog's own
    // fetch (Akhil, 2026-09-16: a pick from the dialog's shortlist showed 100% because only the
    // table's list was consulted). A catalogue-search pick is in neither and stays 1: it is a
    // decision, not a similarity, and the row says "chosen" rather than a percentage for it.
    const text = matchText(line)
    const candidates = [...(matches.get(text)?.candidates ?? []), ...(dialogMatch.data?.get(text)?.candidates ?? [])]
    return candidates.find((c) => c.item_code === itemCode)?.score ?? 1
  }

  /** One line, one write — every interactive entry point (picker, "use this code") lands here. */
  const applyItemToLine = async (line: EnquiryItem, itemCode: string, known?: Item) => {
    noteItemSwap(line, itemCode)
    const patch = await buildItemPatch(line, itemCode, known)
    updateLine.mutate({
      lineId: line.line_id,
      patch: { ...patch, confidence: scoreForCode(line, itemCode) },
    })
    // The human said this wording means this item. `tagWritesForCorrection` drops the case where
    // it already did.
    tagItems.mutate(tagWritesForCorrection([line], itemCode))
  }

  /*
    MANY lines, ONE write — what `AutoMatcher` commits.

    Two changes from calling `applyItemToLine` in a loop, and together they are the difference
    between ~58s and ~3s on a seventeen-line enquiry (every request on this bench costs ~0.85s
    whatever it carries):

      lookups  the item master, blanket orders and supplier list for EVERY line are resolved
               concurrently, instead of two sequential round trips per line;
      write    one `update_enquiry` carrying every patch, instead of a read-modify-write each.
               `items` replaces wholesale, so N lines cost exactly what one line costs.

    A line whose lookups throw is dropped from the batch rather than failing it — it stays
    unmatched and the purchaser matches it by hand, which is what would have happened anyway.
  */
  const applyItemsToLines = async (actions: Array<{ lineId: string; itemCode: string; score?: number }>) => {
    const settled = await Promise.all(
      actions.map(async (action) => {
        const line = lines.find((l) => l.line_id === action.lineId)
        if (!line) return null
        try {
          const patch = await buildItemPatch(line, action.itemCode)
          // The matcher's score is written WITH the item — see `AutoMatchAction.score` for why it
          // cannot be recovered afterwards.
          return {
            lineId: action.lineId,
            patch: action.score === undefined ? patch : { ...patch, confidence: action.score },
          }
        } catch {
          return null
        }
      }),
    )
    const patches = settled.filter((p): p is { lineId: string; patch: EnquiryItemPatch } => p !== null)
    if (patches.length === 0) return 0
    await updateLines.mutateAsync(patches)
    return patches.length
  }

  /*
    Commit the contract plan — every matched line to `route: 'contract'` at its contracted rate,
    in ONE write.

    Three fields travel together and all three persist (measured 2026-08-29): `route`, so the line
    skips RFQ generation; `selected_rate`, the contracted price copied verbatim from the blanket
    order; and `suppliers`, set to the single contracted supplier — which is also what rebuilds
    the selection after a reload, since the order's own name has nowhere to live.

    Where an item is on several selected contracts the CHEAPEST is applied and the rest are
    offered on the row, which is the existing per-line contract dropdown's job.
  */
  const applyContractPlan = async (plan: ReturnType<typeof planContractLines>) => {
    if (plan.length === 0) return
    await updateLines.mutateAsync(
      plan.map((action) => ({
        lineId: action.lineId,
        patch: {
          route: 'contract' as const,
          selected_rate: action.rate,
          suppliers: [action.supplier],
        },
      })),
    )
  }

  /*
    ---- One wait, not four ----

    The table used to appear immediately and then visibly rewrite itself four times: matches land,
    items get applied, prices arrive, contracts price some lines. Each step is legitimate, but
    watching a document edit itself for ten seconds reads as broken rather than busy.

    So the table is held until everything that is going to change has changed, behind a panel that
    NAMES the current step. Two signals, and both are needed:

      `settling`     a request this screen depends on is still in flight;
      `outstanding`  work has been PLANNED but not yet written — the gap between the matcher
                     answering and the write starting, which would otherwise flash the table.

    `outstanding` is derived, not tracked, so it cannot get stuck out of sync: an unmatched line
    that has a candidate is work to do, and it stops being work the moment the write lands.
  */
  /*
    `isLoading`, NEVER `isPending`.

    In React Query v5 a DISABLED query sits at `status: 'pending'` with `fetchStatus: 'idle'`, so
    `isPending` is true forever for a query that will never run. Both of these are disabled in
    ordinary situations — `matchQuery` when every line already has an item (or the search service
    is not configured), `candidatePrices` when there are no candidates to price, which is exactly
    what a reloaded, fully-matched enquiry looks like. Reading `isPending` there left `settling`
    permanently true and the table hidden behind "Loading prices…" indefinitely.

    `isLoading` is `isPending && isFetching`, so it is false for a query that is not running —
    which is the question this actually needs answered.
  */
  const settling =
    matchQuery.isLoading || !itemsResolved || candidatePrices.isLoading || blanketItems.isPending || updateLines.isPending
  const outstanding =
    lines.some((l) => !l.item_code && (matches.get(matchText(l))?.candidates.length ?? 0) > 0) ||
    contractPlan.length > 0

  /*
    Reveal when nothing is left to change — and then NEVER hide again.

    The panel exists for the initial load, where four steps rewrite the table in sequence. It must
    not reappear afterwards: every ordinary edit briefly makes `settling` or `outstanding` true —
    changing an item re-reads the master, picking a contract queues a write — and the table was
    vanishing behind "Preparing the table…" each time, then coming back. That reads as the page
    reloading, and it takes the row you were working on off the screen mid-task.

    A one-way latch. Once the table has been shown for this enquiry it stays shown, and in-flight
    work is signalled by the row's own busy state instead.
  */
  /*
    Keyed on the enquiry, so opening a DIFFERENT one shows its own preparing panel rather than
    inheriting the latch from the last. React Router reuses this component across `:id` changes.

    The ref itself is declared with the other hooks at the top of the component — see its
    declaration. Only the ASSIGNMENT lives here, which is not a hook and may sit after the
    early returns.
  */
  if (settledOnce.current !== null && settledOnce.current !== id) settledOnce.current = null
  if (!settling && !outstanding) settledOnce.current = id
  const ready = revealEarly || settledOnce.current === id || (!settling && !outstanding)

  const preparingStep = matchQuery.isLoading
    ? `Matching ${unmatchedLines.length} line${unmatchedLines.length === 1 ? '' : 's'} to the catalogue…`
    : outstanding || updateLines.isPending
      ? 'Applying matched items…'
      : blanketItems.isPending
        ? 'Loading contract rates…'
        : !itemsResolved
          ? 'Loading item details…'
          : 'Loading prices…'

  /*
    Build and send the contract-priced quotation.

    Priced in the CUSTOMER's currency where they have one, using the same FX snapshot and the same
    margin the comparison screen uses — the margin applied in the company's currency first, then
    converted, so the percentage means what it says whatever the customer is billed in.

    `useCreateQuotation` re-prices an existing draft rather than refusing, and `update_quotation`
    replaces `items` WHOLESALE — which is exactly why `ComparePage` now appends the contract lines
    to its own build. Whichever screen writes last must produce the WHOLE document, or the other
    screen's lines are silently deleted.
  */
  const baseCurrency = baseCurrencyFor(enquiry.header.company)
  // The enquiry's OWN choice first (header card, 2026-09-14), then the customer's currency on
  // file, then the company's — the same precedence the header card shows as its default.
  const quoteCurrency = enquiry.header.customer_currency || customerCurrency.data || baseCurrency
  const quoteFxRate =
    quoteCurrency === baseCurrency
      ? 1
      : (pickRate(exchangeRates.data ?? [], baseCurrency, quoteCurrency)?.rate ?? null)

  const nameByCode = new Map([...itemsByCode].map(([code, item]) => [code, item.name]))
  const pricedQuoteLines = buildContractQuotationLines(
    lines,
    nameByCode,
    pricingRules.data ?? { enforce: false, rules: [] },
    quoteFxRate ?? 1,
  )
  // EVERY line, unpriced ones at zero (Akhil, 2026-09-15) — see `everyEnquiryLine`.
  const contractQuoteLines = everyEnquiryLine(lines, pricedQuoteLines, nameByCode, pricingRules.data ?? { enforce: false, rules: [] })

  const createContractQuotation = () => {
    if (refuseUntilUnitsDecided('create the quotation')) return
    // Same gate as Send RFQ (2026-09-18): the header card, not a 417 from the server, twice.
    if (headerGaps.length > 0) {
      toast(`Before building the quotation, complete the enquiry header — the server requires ${describeMandatory(headerGaps)}. Fill ${headerGaps.length === 1 ? 'it' : 'them'} in the Enquiry details card above.`)
      document.getElementById('enquiry-header-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    if (quoteFxRate === null) {
      // Never quote at a guessed rate — the customer is billed on this number.
      toast(`No exchange rate on file for ${baseCurrency} → ${quoteCurrency}. Add it, or quote in ${baseCurrency}.`)
      return
    }
    const net = contractLinesTotal(contractQuoteLines)
    createQuotation.mutate({
      enquiryId: id,
      existing: existingQuotation.data?.id ?? null,
      customerReferenceNo: enquiry.header.customer_reference_no,
      company: enquiry.header.company,
      enquiryType: enquiry.header.enquiry_type,
      // Moves the enquiry to `Comparison Done` — a provision enquiry never generates an RFQ, so
      // nothing else ever takes it off `Open`, and its board card kept opening this screen while
      // the quotation sat on `/quote/:id`. See `advanceStatus.ts`.
      currentStatus: enquiry.status,
      payload: {
        status: 'Draft',
        revision: 0,
        enquiry: id,
        customer: enquiry.header.customer,
        customer_name: enquiry.header.customer,
        // Site-local, not ISO-UTC — the wire's convention (CLAUDE.md).
        transaction_date: new Date().toISOString().slice(0, 10),
        customer_currency: quoteCurrency,
        exchange_rate: quoteFxRate,
        // No comparison behind a contract price. Verified the method accepts an empty value here
        // (2026-08-31): it reaches its own validation rather than rejecting the field.
        supplier_quote_comparison: '',
        sales_person: enquiry.header.sales_person,
        purchaser: enquiry.header.purchaser,
        vessel: enquiry.header.vessel ?? '',
        delivery_location: enquiry.header.delivery_location ?? '',
        ...(enquiry.header.delivery_port ? { port: enquiry.header.delivery_port } : {}),
        markup: 0,
        net_total: net,
        total: net,
        grand_total: net,
        items: contractQuoteLines,
      },
    })
  }

  /**
   * The master item WITH its stock and contracts merged — what the dialogs should show.
   *
   * `itemsByCode` carries neither: `get_items_by_codes` returns no stock at all (asked for as
   * `D18`), and contracts come from the selected blanket orders. `LineRow` merges them per row;
   * the dialogs were reading the raw item and so displayed "—" for stock we already had in hand.
   */
  const enrichedItem = (code: string | null | undefined): Item | undefined => {
    if (!code) return undefined
    const base = itemsByCode.get(code)
    if (!base) return undefined
    const stock = stockByCode.get(code)
    return mergeItemEnrichment(base, {
      contracts: contractsForItem(rateIndex, code),
      stock: stock ? { stock_enabled: true, ...stock } : undefined,
    })
  }

  /*
    Select-all follows the SEARCH, not the whole enquiry — "find the eight coverall lines, tick
    them, assign a supplier" is the reason the search is worth having. With an empty query
    `shownLines` IS `lines`, so this is unchanged for anyone not using it.

    A line ticked before the query narrowed stays ticked: the bulk actions read `selected`, and
    silently dropping a deliberate choice because the view moved would be worse than keeping it.
  */
  const selectableIds = shownLines.filter(isSelectable).map((l) => l.line_id)
  // How many ticked lines still have no item — the bulk bar says so, see its own note.
  const selectedUnmatched = lines.filter((l) => selected.includes(l.line_id) && !l.item_code).length
  const allSelected = selectableIds.length > 0 && selectableIds.every((lid) => selected.includes(lid))
  const activeLine = dialog && 'lineId' in dialog ? lines.find((l) => l.line_id === dialog.lineId) : undefined
  // Only dim a row while its own request is in flight; `variables` keeps its last value
  // after settling, so the isPending guard is what stops a failed row staying greyed out.
  const busyLineId = updateLine.isPending
    ? updateLine.variables?.lineId
    : deleteLine.isPending
      ? deleteLine.variables
      : undefined

  const toggleSelect = (lineId: string) =>
    setSelected((current) =>
      current.includes(lineId) ? current.filter((x) => x !== lineId) : [...current, lineId],
    )

  /**
   * Applies every OUTSTANDING item-code suggestion and nothing else — item code is the only field
   * this feature can safely apply in one click (see `suggestions.ts`'s `LineSuggestion` header for
   * why the UOM suggestion is display-only and why there is no qty suggestion at all).
   *
   * `buildSuggestions` only reports a code the purchaser has not already set — a line that already
   * has one offers no suggestion — so this cannot overwrite manual work. That restriction is what
   * makes the button pressable at any point during triage, which matters because this app has no
   * undo.
   *
   * Rejected item codes are deliberately excluded: the catalogue does not have them, and applying
   * one would fail the whole document on save. They are reported in the toast instead so the
   * remaining work is named rather than silently dropped.
   */
  const applyAllSuggestions = async () => {
    setApplyingAll(true)
    try {
      let applied = 0
      for (const [lineId, s] of suggestions.byLine) {
        const patch: EnquiryItemPatch = {}
        // Item code only — `customerUom` is display-only (see `LineSuggestion`'s own comment) and
        // there is no `qty` suggestion (a differing qty only ever means the purchaser edited it).
        if (s.itemCode && !s.itemCodeRejected) patch.item_code = s.itemCode
        if (Object.keys(patch).length === 0) continue
        // Sequential, not Promise.all: `updateEnquiryItems` is a read-modify-write over the WHOLE
        // items array, serialized per enquiry by `withEnquiryLock`. Firing them together would
        // queue behind each other anyway, and interleaving reads risks losing an edit.
        await updateLine.mutateAsync({ lineId, patch })
        applied += Object.keys(patch).length
      }
      /*
        The low-confidence count is named, not buried. `searchMatch.ts` now proposes the matcher's
        top hit whatever it scored, so this button can sweep in weak guesses alongside certainties
        — and "Applied 12 suggestions" would read as twelve confident matches. Each flagged row
        stays marked on the table afterwards; this says how many to go back and look at.
      */
      const parts = [`Applied ${applied} suggestion${applied === 1 ? '' : 's'}`]
      if (suggestions.lowConfidence > 0) parts.push(`${suggestions.lowConfidence} low-confidence — worth checking`)
      if (suggestions.needsMatching > 0) parts.push(`${suggestions.needsMatching} still need matching`)
      toast(parts.join(' · '))
    } catch {
      // Don't toast here: `updateLine`'s own `onError` (above) already showed the friendlier,
      // row-aware message (`describeRowLinkError`, or the UOM-master 422) for whatever just
      // failed — and `Toast` holds a single current message, not a queue, so anything we show
      // now would replace it rather than add to it. The loop simply stops; whatever the mutation
      // already applied stays applied.
    } finally {
      setApplyingAll(false)
    }
  }

  return (
    <>
      {/*
        Fills in each unmatched line's item from the matcher's top hit, without a click. Renders
        nothing; see `AutoMatcher` for why it is a component and how it fires once per line.

        Held off while "Accept all" runs — that loop applies a snapshot of the suggestions taken
        at its start, and a write landing underneath it is exactly what `applyingAll` freezes the
        table against.
      */}
      <AutoMatcher
        lines={lines}
        unresolvableCodes={unresolvableCodes}
        matchFor={(line) => matches.get(matchText(line))}
        applyAll={applyItemsToLines}
        // As soon as ANY line has answered — not when every line has (2026-09-17).
        enabled={!applyingAll && matchQuery.answered > 0}
        matches={matches}
        // No toast (Akhil, 2026-09-17): the incremental matcher fired one per batch and they
        // stacked. The rows themselves carry the confidence bar and the low-confidence flag.
      />

      {/* Routes every line a selected contract prices, at its contracted rate, in one write. */}
      <ContractApplier
        plan={contractPlan}
        selection={selectedOrders}
        enabled={!applyingAll && !blanketItems.isPending}
        apply={applyContractPlan}
        onDone={({ priced, withChoices }) =>
          toast(
            withChoices > 0
              ? `${priced} line${priced === 1 ? '' : 's'} priced from contract · ${withChoices} have another contract rate available`
              : `${priced} line${priced === 1 ? '' : 's'} priced from contract`,
          )
        }
      />

      <ContextBar enquiry={enquiry} active="rfq" />
      {/* Every control below is disabled once the enquiry is decided — `lib/readOnly.ts`. */}
      {/*
        The header stays editable at Quotation Sent (2026-09-17): the quotation doctype now
        requires vessel, port, sales person and customer reference on every save, and the enquiry
        header is where they are corrected — a sent quotation missing one cannot be saved until
        the enquiry has it. Won / Rejected / Archived still lock it with the rest.
      */}
      {enquiry.status === 'Quotation Sent' ? <EnquiryHeaderCard enquiry={enquiry} /> : null}
      <ReadOnlyGate reason={lockReason(enquiry.status, 'rfq')}>
      {enquiry.status !== 'Quotation Sent' ? <EnquiryHeaderCard enquiry={enquiry} /> : null}

      <PageHead help="rfq"
        title="Item triage & RFQ Builder"
        subtitle="Enquiries arrive by mail or are logged manually (e.g. a WhatsApp request to a salesman). Provision items on a contract price directly; the rest go to RFQ. Group similar items, set UOM, and assign suppliers in bulk."
      />

      {/*
        Standing contracts. Above the triage banner because it CHANGES those counts — ticking a
        supplier moves lines out of the RFQ column and into provision.
      */}
      <BlanketOrderBar
        orders={blanketOrders.data ?? []}
        selected={selectedOrders}
        loading={blanketItems.isPending}
        matchedLines={contractPricedCount}
        scope={blanketScope}
        selectedLineCount={selected.length}
        onScopeChange={setBlanketScope}
        onChange={(names) => {
          setBlanketChoice(names)
          /*
            Unticking an order takes back the lines it priced (Akhil, 2026-09-16). It used to leave
            them contract-routed "for the purchaser to decide", which read as the clear doing
            nothing — and the same lines re-ticked the order on the next load. `rateIndex` is still
            the OLD selection's here (the state above has not re-rendered yet), which is what
            `revertContractLines` needs to recognise the removed orders' rates.
          */
          const removed = selectedOrders.filter((n) => !names.includes(n))
          const reverts = revertContractLines(lines, rateIndex, removed)
          if (reverts.length === 0) {
            if (names.length === 0) toast('Contracts cleared')
            return
          }
          updateLines.mutate(reverts, {
            onSuccess: () => toast(`${reverts.length} line${reverts.length === 1 ? '' : 's'} back to RFQ — contract price and supplier cleared`),
            onError: (error) => toast(`Contracts unticked, but the lines could not be reverted: ${error.message}`),
          })
        }}
      />

      {/*
        The single wait. Named, so it reads as work rather than a hang, and escapable, so a step
        that never settles (a write that failed and will not be retried) can never trap the
        purchaser on a spinner.
      */}
      {!ready ? (
        <Placeholder>
          <b>Preparing the table…</b>
          <div style={{ marginTop: 6, color: 'var(--muted)' }}>{preparingStep}</div>
          <div style={{ marginTop: 4, color: 'var(--muted)', fontSize: 12 }}>
            Matching runs on the customer's own words and takes about a third of a second per line.
          </div>
          <div style={{ marginTop: 10 }}>
            <Button size="sm" onClick={() => setRevealEarly(true)}>
              Show the table anyway
            </Button>
          </div>
        </Placeholder>
      ) : null}

      {/*
        The enquiry header's gaps, at the top of the page and not only inside the card (Akhil,
        2026-09-17: the comparison refused a quotation for a missing customer reference on an
        enquiry whose RFQ had already gone). Named here, where the purchaser is, with the send
        held until they are filled.
      */}
      {headerGaps.length > 0 ? (
        <div className="banner" style={{ background: 'var(--warnbg)', borderColor: 'var(--warn)' }}>
          ⚠ <b>Enquiry details incomplete:</b> {describeMandatory(headerGaps)} — the server requires {headerGaps.length === 1 ? 'it' : 'them'} on every quotation.
          Fill {headerGaps.length === 1 ? 'it' : 'them'} in the <b>Enquiry details</b> card above before sending an RFQ or building the quotation.
        </div>
      ) : null}
      <div className="banner" style={ready ? undefined : { display: 'none' }}>
        🤖 <b>{counts.total} lines</b> · <Badge tone="violet">{counts.contract} provision</Badge> (contract, no RFQ) ·{' '}
        <Badge tone="info">{counts.rfq} RFQ</Badge> → {counts.rfqCount} RFQ(s). Base price, UOM &amp; on-hand stock
        shown per line.
      </div>

      {/* What these lines were extracted from — so checking a doubtful match doesn't cost a
          round trip to Mail and back. */}
      <SourcePanel
        mailId={enquiry.header.mail_id}
        uploads={enquiryFiles.data ?? []}
        onOpenMail={(mailId, account) => navigate(`/mail?mail=${encodeURIComponent(mailId)}&account=${account}`)}
      />

      {/*
        Everything below is the table and its toolbar — hidden until `ready`, so the purchaser sees
        one settled document instead of four visible rewrites. `display: none` rather than
        unmounting: the rows' own hooks (item masters, stock latches) keep their state, so
        revealing costs nothing and no request is fired twice.
      */}
      <div style={ready ? undefined : { display: 'none' }}>
      {lines.length === 0 ? (
        // `borderStyle: dashed` has to sit on the card itself, where `.card` already
        // supplies the 1px border it modifies (prototype line 663).
        <Card padded={false} style={{ textAlign: 'center', padding: 34, borderStyle: 'dashed' }}>
          <div style={{ fontSize: 28 }}>📄⬆️</div>
          <div style={{ fontWeight: 800, margin: '8px 0 4px' }}>Start this RFQ from a document</div>
          <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 14 }}>
            No mail? Upload the customer's list (Excel / PDF / photo of a WhatsApp message) and we'll extract &amp;
            match the items. Or add items manually.
          </div>
          <UploadButton variant="primary" busy={uploading} onExtract={(file) => void extractUpload(file)} />{' '}
          <Button onClick={() => setDialog({ kind: 'add-item' })}>✚ Add item manually</Button>
        </Card>
      ) : null}

      {/*
        Finding a line on a long enquiry. Purely a VIEW filter — every action below still works on
        the whole enquiry, and "select all" deliberately ticks only what is visible, which is what
        makes the search useful for bulk supplier assignment on a subset.
      */}
      {/* Bulk bar — tick several lines, then act on all of them at once. */}
      <div className="bulkbar">
        {/*
          The search lives HERE, not floating above, because it is part of this workflow rather
          than a separate feature: narrow to the eight coverall lines, tick them all, assign a
          supplier. `selectableIds` follows the filter for exactly that reason, so "select all"
          means "all of what I am looking at".
        */}
        {lines.length > 0 ? (
          <SearchBox
            value={lineSearch}
            onChange={setLineSearch}
            total={lines.length}
            shown={shownLines.length}
            placeholder={`Search ${lines.length} lines…`}
            style={{ marginRight: 4 }}
          />
        ) : null}
        <b>{selected.length}</b> selected ·
        <Button
          size="sm"
          variant="primary"
          disabled={selected.length === 0}
          onClick={() => setDialog({ kind: 'bulk-suppliers' })}
        >
          Assign suppliers…
        </Button>
        {suggestions.applicable + suggestions.needsMatching > 0 ? (
          <Button
            size="sm"
            disabled={suggestions.applicable === 0 || applyingAll}
            onClick={() => void applyAllSuggestions()}
          >
            ✨ Accept all recommendations ({suggestions.applicable})
          </Button>
        ) : null}
        {/* Writes `item_code` via the catalogue picker — frozen with everything else that does
            while "Accept all" runs, so a stale loop snapshot can't race a bulk match. */}
        <Button size="sm" disabled={selected.length < 2 || applyingAll} onClick={() => setDialog({ kind: 'bulk-match' })}>
          Match selected → item…
        </Button>
        {/* Ask the matcher again under the CURRENT company and type — all lines, or the ticked ones. */}
        <Button
          size="sm"
          disabled={rematching || applyingAll || lines.length === 0}
          title={`Search the catalogue again for ${selected.length > 0 ? 'the selected lines' : 'every line'} under ${enquiry?.header.company || 'this company'} / ${enquiry?.header.enquiry_type} — for after the company or enquiry type changed`}
          onClick={() => void rematch(selected.length > 0 ? new Set(selected) : undefined)}
        >
          {rematching ? '🔎 Matching again…' : selected.length > 0 ? `🔎 Re-match selected (${selected.length})` : '🔎 Re-match all'}
        </Button>
        {/* Our catalogue price onto every line, or the ticked ones — `applyOurPriceToLines`. */}
        <Button
          size="sm"
          disabled={stockPricingPlan.actions.length === 0 || updateLines.isPending || applyingAll}
          title={
            stockPricingPlan.actions.length === 0
              ? `No ${selected.length > 0 ? 'selected ' : ''}line has a price of ours left to apply`
              : `Supply ${stockPricingPlan.actions.length} line(s) from our own stock at our catalogue price, each in its own unit — no RFQ. The selling price and margin are set on the quotation.`
          }
          onClick={applyOurPriceToLines}
        >
          🏷 Use our price — {selected.length > 0 ? 'selected' : 'all'} ({stockPricingPlan.actions.length})
        </Button>
        <Button size="sm" variant="danger" disabled={selected.length === 0 || deleteLines.isPending} onClick={deleteSelected}>
          {deleteLines.isPending ? 'Removing…' : `Delete selected${selected.length > 0 ? ` (${selected.length})` : ''}`}
        </Button>
        {/* Status, not a trigger — it runs by itself. Shown only while there is something to
            say, because "matching…" on a fully-matched enquiry is noise. */}
        {unmatchedLines.length > 0 && matchQuery.isFetching ? (
          <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>
            🔎 finding catalogue items — {matchQuery.pending} line(s) still being asked, each lands as it answers (about 16s a line, three at a time)…
          </span>
        ) : null}
        {matchQuery.isError ? (
          <span style={{ color: 'var(--warn)', fontSize: 11.5 }}>
            Item matcher unavailable — match by hand.{' '}
            <button className="linkish" onClick={() => void matchQuery.refetch()}>
              retry
            </button>
          </span>
        ) : null}
        <Button
          size="sm"
          disabled={syncFromErp.isPending || !lines.some((l) => l.item_code)}
          title={`Pull the latest price and stock from the ERP for ${selected.length > 0 ? 'the selected lines' : 'every matched line'}`}
          onClick={() => startErpSync(lines.filter((l) => l.item_code && (selected.length === 0 || selected.includes(l.line_id))).map((l) => l.item_code as string))}
        >
          {syncFromErp.isPending ? '↻ Syncing from ERP…' : selected.length > 0 ? `↻ Sync selected from ERP (${selected.length})` : '↻ Sync prices & stock from ERP'}
        </Button>
        <Button size="sm" onClick={() => setSelected([])}>
          Clear
        </Button>
        <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>
          Tick several lines → assign <b>multiple suppliers to all at once</b>, or set them{' '}
          <b>all to the same matched item</b> (each line keeps its own qty).
        </span>
      </div>

      {/*
        Unmatched lines are selectable now (see `isSelectable`), which is what makes bulk MATCHING
        reachable at all. The cost is that a supplier can be assigned to a line with no item yet —
        harmless, but it would otherwise look finished and then silently produce no RFQ, because
        `buildRfqPlan` needs both. Said here, at the moment of selecting, rather than discovered at
        the generate step.
      */}
      {selectedUnmatched > 0 ? (
        <div style={{ margin: '-6px 0 12px', fontSize: 11.5, color: 'var(--warn)' }}>
          {selectedUnmatched} of the {selected.length} ticked line(s) have no matched item yet. You can still assign
          suppliers — no RFQ goes out for them until they are matched.
        </div>
      ) : null}

      <Card padded={false}>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 24 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    aria-label="Select all RFQ lines"
                    onChange={() => setSelected(allSelected ? [] : selectableIds)}
                  />
                </th>
                <th title="Customer's serial number exactly as sent in the mail / image — read-only">Cust. S.No</th>
                <th>From customer</th>
                <th>Matched item</th>
                {/* Qty and unit beside the item they belong to (Akhil, 2026-10-26). */}
                <th>Qty · UOM</th>
                {/*
                  A filter IN the column head, because that is where the question is asked: "which
                  of these still has no route?" is about this column, and a control anywhere else
                  has to name the column to be understood.

                  It SORTS rather than hides — see `sortForTriage`. The count is on the control so
                  it says what it will do before it is pressed, and a column with nothing
                  outstanding shows no control at all rather than an inert one.
                */}
                <th>
                  Route <TriageToggle column="route" lines={lines} active={triage} onChange={setTriage} />
                </th>
                <th>Conf.</th>
                <th>In stock</th>
                <th>
                  Suppliers <TriageToggle column="supplier" lines={lines} active={triage} onChange={setTriage} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shownLines.map((line) => {
                const item = line.item_code ? itemsByCode.get(line.item_code) : undefined
                return (
                  <LineRow
                    key={line.line_id}
                    line={line}
                    enquiryId={id}
                    item={item}
                    priceSource={line.item_code ? priceSource.get(line.item_code) : undefined}
                    ourPrice={ourPriceFor(line)}
                    candidatePrices={candidatePrices.data}
                    // From the selected contracts — no per-row request. See `LineRow`'s own note.
                    blanketContracts={contractsForItem(rateIndex, line.item_code)}
                    onSyncFromErp={line.item_code ? () => startErpSync([line.item_code as string]) : undefined}
                    syncingFromErp={!!line.item_code && syncingCodes.has(line.item_code)}
                    /* Every contract that covers this item, selected or not — the row shows the
                       best one it is NOT already priced from, so a better deal is visible without
                       ticking contracts one by one to find it. */
                    availableContracts={contractsForItem(allRateIndex, line.item_code)}
                    stockByCode={stockByCode}
                    itemPending={!itemsResolved}
                    selected={selected.includes(line.line_id)}
                    selectable={isSelectable(line)}
                    busy={busyLineId === line.line_id}
                    uomError={uomErrors[line.line_id] ?? null}
                    supplierName={supplierName}
                    // The row's existing shortlist UI, fed from whichever source has an answer:
                    // the extraction's own suggestion, plus catalogue candidates from the matcher.
                    suggestion={mergeMatchIntoSuggestion(suggestions.byLine.get(line.line_id), matches.get(matchText(line)))}
                    // Frozen for the whole table while the batch runs — see `applyAllSuggestions`'s
                    // own comment for why a single stale snapshot makes this necessary.
                    disabled={applyingAll}
                    onToggleSelect={() => toggleSelect(line.line_id)}
                    onOpenCatalog={() => setDialog({ kind: 'match', lineId: line.line_id })}
                    onOpenSuppliers={() => setDialog({ kind: 'line-suppliers', lineId: line.line_id })}
                    onChangeQty={(qty) => updateLine.mutate({ lineId: line.line_id, patch: { qty } })}
                    uomChoice={uomChoiceFor(line)}
                    onDecideUom={(uom) => decideUom(line, uom)}
                    flash={flashLineId === line.line_id}
                    onChangeUom={(uom) => {
                      /*
                        An in-house line's price is PER UNIT, so it moves with the unit (Akhil,
                        2026-09-14): 12/Nos becomes 288/CASE in the same write, never 12/CASE.
                        A unit the master cannot convert leaves the rate alone — the write itself
                        fails Link validation for such a unit, and the row says so.
                      */
                      const repriced = line.suppliers.includes(IN_HOUSE_SUPPLIER) ? ourPriceFor(line, uom) : null
                      updateLine.mutate({
                        lineId: line.line_id,
                        patch: repriced !== null ? { uom, selected_rate: repriced.amount } : { uom },
                      })
                    }}
                    onChangeDescription={(description) => updateLine.mutate({ lineId: line.line_id, patch: { description } })}
                    // Not `{ route }` alone: leaving the contract drops its price and its supplier too —
                    // see `routeChangePatch`.
                    onChangeRoute={(route) => updateLine.mutate({ lineId: line.line_id, patch: routeChangePatch(line, route, allRateIndex) })}
                    onChangeContract={(contractId) => {
                      /*
                        Choosing a different blanket order must move the RATE, not just a label.

                        This used to write `contract_id` alone — a field `applyItemPatch` drops on
                        purpose, because the wire has nowhere to put it. So picking the cheaper of
                        two contracts changed the dropdown and nothing else: the line kept the
                        first contract's `selected_rate`, and that is the number the customer is
                        quoted. Silently wrong, which is the worst kind.

                        `selected_rate` and `suppliers` both persist, so the choice is written the
                        same way `applyContractPlan` writes the initial one.
                      */
                      /*
                        Looked up across EVERY active contract, not only the ticked ones.

                        It used to search `rateIndex` (the selected orders), so a contract the row
                        listed but the purchaser had not ticked could not be picked — the click
                        found nothing and returned silently. The row now shows every contract that
                        covers the item, so the handler has to be able to price from any of them.
                      */
                      const picked = contractsForItem(allRateIndex, line.item_code).find((c) => c.id === contractId)
                      if (!picked) return
                      updateLine.mutate({
                        lineId: line.line_id,
                        patch: {
                          contract_id: contractId,
                          // `route` too: picking a contract for an RFQ-routed line is what MOVES it
                          // onto the contract, and without this the rate landed on a line still
                          // heading for an RFQ nobody needed to send.
                          route: 'contract',
                          selected_rate: picked.rate.amount,
                          suppliers: [picked.supplier],
                        },
                      })
                    }}
                    onRemoveSupplier={(supplier) =>
                      updateLine.mutate({
                        lineId: line.line_id,
                        patch: { suppliers: line.suppliers.filter((s) => s !== supplier) },
                      })
                    }
                    onDelete={() => deleteLine.mutate(line.line_id)}
                    sentOnRfq={sentRows.get(line.cust_sno) ?? null}
                    onUseItemCode={(code) => void applyItemToLine(line, code)}
                    // The request's name onto the line, so it reads as requested after a reload.
                    onItemRequested={(requestName) => updateLine.mutate({ lineId: line.line_id, patch: { item_creation_req_ref: requestName } })}
                    onOpenHistory={() => setDialog({ kind: 'item-history', lineId: line.line_id })}
                    /*
                      The in-house badge on the row prices the line directly (Akhil, 2026-09-17)
                      — the same three-field write the history dialog's "Use our stock" makes
                      (`priceFromStock`), minus the dialog. Absent when there is no figure to
                      offer, or the line is already priced from our stock at it.
                    */
                    onUseOurStock={(() => {
                      const found = ourPriceFor(line)
                      const patch = found ? priceFromStock(found.amount) : null
                      if (!patch) return undefined
                      if (line.suppliers.includes(IN_HOUSE_SUPPLIER) && line.route === 'contract' && line.selected_rate === patch.selected_rate) return undefined
                      return () => {
                        updateLine.mutate({ lineId: line.line_id, patch })
                        toast(`Priced from our own stock at ${found!.amount.toFixed(2)} ${found!.currency}/${found!.uom} — no RFQ for this line`)
                      }
                    })()}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
        {!itemsResolved && lines.length > 0 ? (
          <div className="pad" style={{ color: 'var(--muted)', fontSize: 12 }}>
            Loading item master…
          </div>
        ) : null}
      </Card>

      <div className="row" style={{ marginTop: 14, flexWrap: 'wrap' }}>
        {/* Opens the review; sending happens from there. An RFQ read by a supplier cannot be
            unsent, so the split is shown before it goes out rather than after. */}
        <Button
          variant="primary"
          disabled={counts.rfqCount === 0 || sendingAll || headerGaps.length > 0 || noQtyLines.length > 0}
          title={
            headerGaps.length > 0
              ? `Enquiry details incomplete: ${describeMandatory(headerGaps)}`
              : noQtyLines.length > 0
                ? `Set a quantity on line${noQtyLines.length === 1 ? '' : 's'} ${noQtyLines.map((l) => l.cust_sno).join(', ')} first`
                : undefined
          }
          onClick={openSendDialog}
        >
          Generate {counts.rfqCount} RFQ(s) &amp; send →
        </Button>
        {/*
          Quote the contract lines directly.

          This used to be a link to `/quote/:id` labelled "priced in quotation" — which was not
          true: nothing produced those lines. The only path to a quotation was the comparison
          screen, and `buildQuotationPayload` prices from ALLOCATIONS, which exist only where a
          supplier quoted. So contract lines skipped the RFQ correctly and then vanished, and the
          button pointed at a document that did not contain them.

          A blanket order fixes the rate, so there is nothing to compete and nothing to wait for.
          One deliberate press, rather than creating a numbered customer document as a side effect
          of ticking a contract.
        */}
        {contractQuoteLines.length > 0 ? (
          <Button
            variant="primary"
            disabled={createQuotation.isPending || headerGaps.length > 0}
            onClick={createContractQuotation}
            title={headerGaps.length > 0 ? `Enquiry details incomplete: ${describeMandatory(headerGaps)}` : `Every matched line goes on the quotation — ${pricedQuoteLines.length} priced from contracts or our stock, the rest at 0.00 to be priced there. No RFQ, no supplier quotation.`}
          >
            {createQuotation.isPending
              ? 'Creating quotation…'
              : `${existingQuotation.data ? 'Update' : 'Create'} quotation · ${contractQuoteLines.length} line(s), ${pricedQuoteLines.length} priced`}
          </Button>
        ) : (
          <Button onClick={() => navigate(`/quote/${id}`)} disabled={counts.contract === 0}>
            {counts.contract} provision line(s) → open quotation
          </Button>
        )}
        <Button onClick={() => setDialog({ kind: 'add-item' })}>✚ Add item + suppliers</Button>
        <UploadButton busy={uploading} onExtract={(file) => void extractUpload(file)} />
{/*
          Point 14's item half, offered UNCONDITIONALLY.

          It is also on the line (`RequestItemLink` in `LineRow`), but that placement depends on a
          line being unmatched — and the matcher returns a candidate at 3% confidence rather than
          nothing, so "unmatched" is rarer than the need it stands for. A purchaser who can see the
          catalogue has the wrong item does not need permission from a badge to say so.
        */}
        <RequestItemLink description="" uom="" enquiryId={id} as="button" />
      </div>

      {/* RFQ split — derived on the client, so it updates as suppliers are ticked. */}
      <Card className="pad" style={{ marginTop: 14 }}>
        <b>RFQ split (RFQ lines only)</b>
        <p style={{ color: 'var(--muted)', fontSize: 12.5, margin: '6px 0 10px' }}>
          One RFQ per supplier. A line assigned to <b>multiple suppliers</b> appears in each of their RFQs (competing
          quotes). Tagged <b>{enquiry.ref}</b>.
        </p>
        {groups.length === 0 ? (
          <div style={{ color: 'var(--muted)' }}>Assign suppliers to RFQ lines to see the split.</div>
        ) : (
          groups.map((group) => {
            // Show the real RFQ number when one has already been issued to this supplier;
            // otherwise say "New RFQ" — the ID is the server's to assign, never guessed here.
            // `r.supplier` and `group.supplier` are both codes (task 8b) — comparing a code
            // against a display name here used to silently never match.
            const existing = rfqsQuery.data?.find((r) => r.supplier === group.supplier)
            return (
              <div className="kv" key={group.supplier}>
                <span className="lab">
                  <b style={{ color: 'var(--ink)' }}>{existing?.id ?? 'New RFQ'}</b> → {supplierName(group.supplier)}
                </span>
                <span>
                  {group.lines.length} item(s) · <Badge tone="info">protected Excel</Badge>
                </span>
              </div>
            )
          })
        )}
        {unassigned.length ? (
          <div className="kv">
            <span className="lab" style={{ color: 'var(--warn)' }}>
              ⚠ {unassigned.length} RFQ line(s) still need a supplier
            </span>
          </div>
        ) : null}
      </Card>
      </div>

      {/* ---------- dialogs ---------- */}

      {dialog?.kind === 'match' && activeLine ? (
        <CatalogPickerModal
          title="Change / match item"
          enquiryType={enquiry.header.enquiry_type}
          // What the matcher already worked out for THIS line, pinned above the search.
          // The table's own results where they cover this line, else the dialog's own fetch.
          suggested={
            cachedDialogMatch?.candidates.length
              ? cachedDialogMatch.candidates
              : dialogMatch.data?.get(matchText(activeLine))?.candidates
          }
          suggestedPending={dialogMatch.isPending}
          onClose={() => setDialog(null)}
          onPick={(item) => {
            // Item + unit + the item's suppliers, in one write — see `applyItemToLine`.
            void applyItemToLine(activeLine, item.code, item)
            setDialog(null)
          }}
        />
      ) : null}

      {dialog?.kind === 'bulk-match' ? (
        <CatalogPickerModal
          title={`Match ${selected.length} selected lines to one item`}
          intro="Pick the catalogue item all selected lines should map to (e.g. several free-text medicine lines → one master item). Each line stays separate with its own qty — only their matched item changes."
          enquiryType={enquiry.header.enquiry_type}
          onClose={() => setDialog(null)}
          onPick={(item) => {
            // Every selected line gets the same item, so every one of them can be left holding a
            // unit that item does not accept — the single-line defect, multiplied. Resolved per
            // line (each has its own wording and its own current unit) and sent in the one write.
            const accepted = uomOptions(item).map((c) => c.uom)
            const uomByLine: Record<string, string> = {}
            for (const lineId of selected) {
              const line = lines.find((l) => l.line_id === lineId)
              if (!line) continue
              noteItemSwap(line, item.code)
              const uom = uomAfterItemChange(line.uom, matchText(line), accepted, item.base_uom, line.customer_uom)
              if (uom) uomByLine[lineId] = uom
            }
            // One supplier lookup for the whole batch — every line is getting the same item.
            // Skipped when nothing in the selection will use them: `isSelectable` already keeps
            // contract lines out of bulk actions, so this is belt and braces.
            const anyRfqLine = selected.some((id) => lines.find((l) => l.line_id === id)?.route !== 'contract')
            void (anyRfqLine ? fetchSuppliersForItem(item.code) : Promise.resolve<string[]>([])).then((suppliers) =>
              matchLines.mutate({ line_ids: selected, item_code: item.code, uom_by_line: uomByLine, suppliers }),
            )
            // Every selected line's wording is a synonym for this one item — which is exactly what
            // a bulk match asserts. De-duplicated in `toItemTagsBody`, so five lines reading the
            // same thing are one tag.
            tagItems.mutate(
              tagWritesForCorrection(
                selected.flatMap((lineId) => lines.find((l) => l.line_id === lineId) ?? []),
                item.code,
              ),
            )
          }}
        />
      ) : null}

      {dialog?.kind === 'line-suppliers' && activeLine ? (
        <SupplierPickerModal
          company={enquiry.header.company}
          title={`Suppliers for ${itemsByCode.get(activeLine.item_code ?? '')?.name ?? activeLine.item_code}`}
          intro="Tick every supplier you want to send this item to — one item, many suppliers, in one go. Each gets it as a competing quote."
          item={enrichedItem(activeLine.item_code)}
          initialSelected={activeLine.suppliers}
          onClose={() => setDialog(null)}
          onSave={(suppliers) =>
            // Toast from the mutation's own onSuccess, not here — otherwise a failed save
            // shows "set" and the error together.
            updateLine.mutate(
              { lineId: activeLine.line_id, patch: { suppliers } },
              {
                onSuccess: () => {
                  setDialog(null)
                  toast(`${suppliers.length} supplier(s) set`)
                },
              },
            )
          }
        />
      ) : null}

      {dialog?.kind === 'bulk-suppliers' ? (
        <SupplierPickerModal
          company={enquiry.header.company}
          title={`Assign suppliers to ${selected.length} selected item(s)`}
          // The lines by serial, not just a count — see `describeSelectedLines`.
          intro={`${describeSelectedLines(lines, selected, sentRows)}. Tick every supplier to add to these lines in one go. (Contract lines in the selection are skipped.)`}
          initialSelected={[]}
          onClose={() => setDialog(null)}
          onSave={(suppliers) => assignSuppliers.mutate({ line_ids: selected, suppliers })}
          // What the selected lines already carry, so one can be taken off them all — or all
          // of them cleared — from the same place they were put on (Akhil, 2026-09-15).
          current={(() => {
            const counts = new Map<string, number>()
            for (const line of lines) {
              if (!selected.includes(line.line_id) || line.route !== 'rfq') continue
              for (const s of line.suppliers) counts.set(s, (counts.get(s) ?? 0) + 1)
            }
            return [...counts].map(([supplier, n]) => ({ supplier, lines: n })).sort((a, b) => b.lines - a.lines)
          })()}
          onRemove={(supplier) => assignSuppliers.mutate({ line_ids: selected, suppliers: [supplier], mode: 'remove' })}
          onClear={() => {
            if (window.confirm(`Remove every supplier from the ${selected.length} selected line(s)?`)) {
              assignSuppliers.mutate({ line_ids: selected, suppliers: [], mode: 'clear' })
            }
          }}
        />
      ) : null}

      {dialog?.kind === 'item-history' && activeLine?.item_code ? (
        <ItemHistoryModal
          itemCode={activeLine.item_code}
          itemName={itemsByCode.get(activeLine.item_code)?.name ?? activeLine.item_code}
          uom={activeLine.uom}
          available={stockByCode.get(activeLine.item_code)?.available}
          // Per the LINE's unit, not the catalogue's base — a line asking in cases is priced per
          // case (Akhil, 2026-09-14). `null` when the master cannot convert to that unit.
          ourPrice={(() => {
            const found = ourPriceFor(activeLine)
            return found ? { amount: found.amount, currency: found.currency as Currency } : null
          })()}
          contracts={contractsForItem(rateIndex, activeLine.item_code)}
          rfqSuppliers={activeLine.suppliers}
          onPrice={(patch, label) => {
            /*
              Price the line from a past quote or from our own shelf — the same three fields a
              blanket order writes, because it means the same thing: this line skips the RFQ and
              goes straight onto the customer quotation at a known cost.

              No supplier quotation is created. That supplier has not quoted THIS enquiry, and
              filing a document saying they had would put a fiction in the audit trail.
            */
            setDialog(null)
            updateLine.mutate({ lineId: activeLine.line_id, patch })
            toast(`Priced from ${label} — no RFQ for this line`)
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === 'add-item' ? (
        <AddItemModal
          enquiryType={enquiry.header.enquiry_type}
          lines={lines}
          busy={addLine.isPending}
          onClose={() => setDialog(null)}
          // An item already on the enquiry is NOT refused here any more (Akhil, 2026-10-16): the
          // dialog names the line that has it and asks, because a second line is a variant.
          onAdd={(line) => addLine.mutate(line)}
        />
      ) : null}

      {drafts ? (
        <SendRfqModal
          drafts={drafts}
          itemsByCode={itemsByCode}
          enquiryId={id}
          sending={sendingAll}
          onChange={patchDraft}
          onSend={() => void sendAllDrafts()}
          onClose={() => setDrafts(null)}
        />
      ) : null}
      </ReadOnlyGate>
    </>
  )
}

/**
 * Upload → extract. Posts the real file as multipart, exactly as contract §5 specifies;
 * the mock ignores the bytes and returns a fixed sample, but the request shape is the one
 * the production AI service will receive.
 *
 * A3 (docs/bosun-required-api-round-2.md) — no live endpoint yet for triggering
 * `mail.extract_enquiry_items` from a raw file with no existing mail behind it (see
 * `lib/api/liveAvailability.ts`). The control disables itself rather than firing a request
 * that would 404 against live.
 */
/**
 * The little "N need one" chip in a column head. Toggles that column's undecided lines to the top.
 *
 * Renders NOTHING when the column has nothing outstanding: a disabled control that would do
 * nothing still asks to be understood, and on a fully-triaged enquiry there is no question to ask.
 */
function TriageToggle({
  column,
  lines,
  active,
  onChange,
}: {
  column: TriageFilter
  lines: EnquiryItem[]
  active: TriageFilter | null
  onChange: (next: TriageFilter | null) => void
}) {
  const count = countUndecided(lines, column)
  if (count === 0) return null

  const on = active === column
  return (
    <button
      type="button"
      onClick={() => onChange(on ? null : column)}
      title={
        on
          ? 'Showing only these — click to show every line again'
          : `Show only the ${count} line(s) still needing one`
      }
      style={{
        marginLeft: 6,
        border: `1px solid ${on ? 'var(--brand)' : 'var(--line)'}`,
        background: on ? 'var(--brand)' : 'var(--panel)',
        color: on ? '#fff' : 'var(--warn)',
        borderRadius: 999,
        padding: '1px 7px',
        fontSize: 10.5,
        fontWeight: 700,
        cursor: 'pointer',
        verticalAlign: 'middle',
      }}
    >
      {on ? `${count} only` : `${count} \u26a0`}
    </button>
  )
}

function UploadButton({
  busy,
  onExtract,
  variant = 'default',
}: {
  busy: boolean
  onExtract: (file: File) => void
  variant?: 'default' | 'primary'
}) {
  const available = isLiveFeature('enquiryExtractUpload')
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <label
        className={`btn${variant === 'primary' ? ' pri' : ''}`}
        style={{ cursor: available ? 'pointer' : 'not-allowed', opacity: available ? 1 : 0.55 }}
        title={available ? undefined : NOT_AVAILABLE_YET}
      >
        {busy ? 'Extracting…' : '⬆ Upload list (Excel, CSV, Word, PDF, photo) → extract items'}
        <input
          type="file"
          // Images and scans go to Document Intelligence — see `readDocument.ts`.
          accept=".xlsx,.xlsm,.xls,.csv,.docx,.doc,.pdf,image/*"
          hidden
          disabled={!available}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) onExtract(file)
            // Reset so re-picking the same file fires change again.
            e.target.value = ''
          }}
        />
      </label>
      {available ? null : (
        <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>{NOT_AVAILABLE_YET}</span>
      )}
    </span>
  )
}


